import type { EntityMeta } from '@/types/entity';
import type { ViewTemplateDSL, ViewType } from '@/lib/use-view-template';
import {
  FINANCE_BILL_ENTRY_LIST_COLUMNS,
  FINANCE_JOURNAL_ENTRY_LIST_COLUMNS,
  FINANCE_BILL_LINE_LIST_COLUMNS,
  FINANCE_JOURNAL_LINE_LIST_COLUMNS,
  FINANCE_BILL_ENTRY_FORM_FIELDS,
  FINANCE_JOURNAL_ENTRY_FORM_FIELDS,
  FINANCE_BILL_LINE_FORM_FIELDS,
  FINANCE_JOURNAL_LINE_FORM_FIELDS,
  FINANCE_BILL_ENTRY_LIST_TEMPLATE,
  FINANCE_JOURNAL_ENTRY_LIST_TEMPLATE,
  FINANCE_BILL_ENTRY_FORM_TEMPLATE,
  FINANCE_JOURNAL_ENTRY_FORM_TEMPLATE,
  FINANCE_BILL_LINE_FORM_TEMPLATE,
  FINANCE_JOURNAL_LINE_FORM_TEMPLATE,
  FINANCE_BILL_ENTRY_LIST_CLASSIC_TEMPLATE,
  FINANCE_JOURNAL_ENTRY_LIST_CLASSIC_TEMPLATE,
  FINANCE_BILL_ENTRY_DETAIL_CLASSIC_TEMPLATE,
  FINANCE_JOURNAL_ENTRY_DETAIL_CLASSIC_TEMPLATE,
  FINANCE_AR_RECEIPT_LIST_CLASSIC_TEMPLATE,
  FINANCE_AR_RECEIPT_DETAIL_CLASSIC_TEMPLATE,
  FINANCE_AP_PAYMENT_LIST_CLASSIC_TEMPLATE,
  FINANCE_AP_PAYMENT_DETAIL_CLASSIC_TEMPLATE,
} from '@/lib/finance-view-contracts';
import {
  HMS_JOB_LIST_TEMPLATE,
  HMS_JOB_DETAIL_TEMPLATE,
  HMS_JOB_FORM_TEMPLATE,
  INTAKE_CASES_LIST_TEMPLATE,
  HMS_JOB_DETAIL_CLASSIC_TEMPLATE,
  HMS_JOB_LIST_CLASSIC_TEMPLATE,
} from '@/lib/hms-view-contracts';
import i18next from 'i18next';

const PRO2_I18N: Record<string, { zh?: string; ja?: string }> = {
  'Job No': { zh: '工作号', ja: 'ジョブ番号' },
  ETA: { zh: '预计到港', ja: '到着予定' },
  ETD: { zh: '预计开航', ja: '出発予定' },
  Vessel: { zh: '船名', ja: '船名' },
  Voyage: { zh: '航次', ja: '航海番号' },
  POL: { zh: '起运港', ja: '積出港' },
  POD: { zh: '卸货港', ja: '仕向港' },
  Agent: { zh: '代理', ja: '代理店' },
  Created: { zh: '创建时间', ja: '作成日時' },
  Container: { zh: '柜号', ja: 'コンテナ' },
  Seal: { zh: '封条号', ja: 'シール番号' },
  Size: { zh: '尺寸', ja: 'サイズ' },
  Type: { zh: '类型', ja: 'タイプ' },
  MBL: { zh: '主提单', ja: 'MBL' },
  'BL No': { zh: '提单号', ja: 'BL番号' },
  Booking: { zh: '订舱号', ja: 'ブッキング' },
  'Booking No': { zh: '订舱号', ja: 'ブッキング番号' },
  DG: { zh: '危品', ja: '危険品' },
  'Dest Port': { zh: '目的港', ja: '仕向港' },
  Shipper: { zh: '发货人', ja: '荷送人' },
  'Cargo Type': { zh: '货物类型', ja: '貨物タイプ' },
  Commodity: { zh: '品名', ja: '品目' },
  Pkgs: { zh: '件数', ja: '梱包数' },
  Packages: { zh: '件数', ja: '梱包数' },
  Unit: { zh: '单位', ja: '単位' },
  KGS: { zh: '公斤', ja: 'KGS' },
  CBM: { zh: '立方', ja: 'CBM' },
  Cargo: { zh: '货物', ja: '貨物' },
  From: { zh: '起点', ja: '起点' },
  To: { zh: '终点', ja: '終点' },
  Description: { zh: '描述', ja: '説明' },
  'Cargo Description': { zh: '货物描述', ja: '貨物説明' },
  Header: { zh: '抬头', ja: 'ヘッダー' },
  Status: { zh: '状态', ja: '状態' },
  'Doc No': { zh: '单号', ja: '伝票番号' },
  'Job File Id': { zh: '工作档ID', ja: 'ジョブファイルID' },
  'Doc Type': { zh: '单据类型', ja: '伝票タイプ' },
  Company: { zh: '公司', ja: '会社' },
  'Company Name': { zh: '公司名称', ja: '会社名' },
  'Issue Date': { zh: '开单日期', ja: '発行日' },
  Curr: { zh: '币别', ja: '通貨' },
  Total: { zh: '总额', ja: '合计' },
  Outstanding: { zh: '未结', ja: '未回収' },
  PDF: { zh: 'PDF数', ja: 'PDF数' },
  Email: { zh: '邮件数', ja: 'メール数' },
  'Company Code': { zh: '公司代码', ja: '会社コード' },
  'Job Date': { zh: '业务日期', ja: '業務日' },
  'Payment Date': { zh: '付款日期', ja: '支払日' },
  Currency: { zh: '币别', ja: '通貨' },
  'PDF Count': { zh: 'PDF数量', ja: 'PDF件数' },
  'Email Count': { zh: '邮件数量', ja: 'メール件数' },
  Amounts: { zh: '金额', ja: '金額' },
  'Local Amount': { zh: '本币金额', ja: '現地通貨金額' },
  Line: { zh: '行号', ja: '行番号' },
  'Charge Code': { zh: '费用代码', ja: 'チャージコード' },
  'Charge Name': { zh: '费用名称', ja: 'チャージ名' },
  Rate: { zh: '费率', ja: 'レート' },
  Qty: { zh: '数量', ja: '数量' },
  Amount: { zh: '金额', ja: '金額' },
  'Local Amt': { zh: '本币金额', ja: '現地通貨額' },
  Tax: { zh: '税', ja: '税' },
  'Left Curr': { zh: '左币别', ja: '左通貨' },
  'Left Rate': { zh: '左汇率', ja: '左レート' },
  'Right Curr': { zh: '右币别', ja: '右通貨' },
  'Right Rate': { zh: '右汇率', ja: '右レート' },
  Code: { zh: '代码', ja: 'コード' },
  'Short Name': { zh: '简称', ja: '略称' },
  Port: { zh: '港口', ja: '港' },
  Airport: { zh: '机场', ja: '空港' },
  Active: { zh: '启用', ja: '有効' },
  Statement: { zh: '对账单', ja: '明細' },
  Global: { zh: '全局', ja: 'グローバル' },
  'Email Notice': { zh: '邮件通知', ja: 'メール通知' },
  'Last Txn': { zh: '最后业务', ja: '最終取引' },
  Identity: { zh: '基本信息', ja: '基本情報' },
  Routing: { zh: '航线信息', ja: '航路情報' },
  Address: { zh: '地址', ja: '住所' },
  'Monthly Statement': { zh: '月结', ja: '月次締め' },
  'Port Code': { zh: '港口代码', ja: '港コード' },
  'Airport Code': { zh: '机场代码', ja: '空港コード' },
  'Last Transaction': { zh: '最后业务日期', ja: '最終取引日' },
  'Import Shipments': { zh: '进口货件', ja: '輸入貨件' },
  'CMS PRO2 sea import Job File read-only browser': { zh: 'CMS PRO2 海运进口工作档只读浏览', ja: 'CMS PRO2 海上輸入ジョブファイル参照' },
  'Job No / Vessel / Voyage / POL / Agent…': { zh: '工作号 / 船名 / 航次 / 起运港 / 代理…', ja: 'ジョブ番号 / 船名 / 航海番号 / POL / 代理店…' },
  'Send NOA': { zh: '发送到港通知', ja: 'NOA送信' },
  'Draft Arrival Email': { zh: '生成到港邮件草稿', ja: '到着メール草稿' },
  'Generate Arrival Note': { zh: '生成到港通知单', ja: '到着通知書生成' },
  'Export Shipments': { zh: '出口货件', ja: '輸出貨件' },
  'CMS PRO2 sea export Job File read-only browser': { zh: 'CMS PRO2 海运出口工作档只读浏览', ja: 'CMS PRO2 海上輸出ジョブファイル参照' },
  'Send Shipment Update': { zh: '发送货件更新', ja: '出荷更新送信' },
  'Draft BL Follow-up': { zh: '生成提单跟进草稿', ja: 'BLフォロー草稿' },
  'Import Containers': { zh: '进口柜', ja: '輸入コンテナ' },
  'CMS PRO2 sea import container read-only browser': { zh: 'CMS PRO2 海运进口柜只读浏览', ja: 'CMS PRO2 海上輸入コンテナ参照' },
  'Job No / Container / Seal / MBL / Vessel…': { zh: '工作号 / 柜号 / 封条 / 主提单 / 船名…', ja: 'ジョブ番号 / コンテナ / シール / MBL / 船名…' },
  'Export Containers': { zh: '出口柜', ja: '輸出コンテナ' },
  'CMS PRO2 sea export container read-only browser': { zh: 'CMS PRO2 海运出口柜只读浏览', ja: 'CMS PRO2 海上輸出コンテナ参照' },
  'Import Cargo': { zh: '进口货物', ja: '輸入貨物' },
  'CMS PRO2 sea import cargo read-only browser': { zh: 'CMS PRO2 海运进口货物只读浏览', ja: 'CMS PRO2 海上輸入貨物参照' },
  'Job No / BL / Container / Cargo…': { zh: '工作号 / 提单 / 柜号 / 货物…', ja: 'ジョブ番号 / BL / コンテナ / 貨物…' },
  'Export Cargo': { zh: '出口货物', ja: '輸出貨物' },
  'CMS PRO2 sea export cargo read-only browser': { zh: 'CMS PRO2 海运出口货物只读浏览', ja: 'CMS PRO2 海上輸出貨物参照' },
  'Job No / Booking / BL / Container / Cargo…': { zh: '工作号 / 订舱 / 提单 / 柜号 / 货物…', ja: 'ジョブ番号 / ブッキング / BL / コンテナ / 貨物…' },
  'Export Bookings': { zh: '出口订舱', ja: '輸出ブッキング' },
  'CMS PRO2 sea export booking read-only browser': { zh: 'CMS PRO2 海运出口订舱只读浏览', ja: 'CMS PRO2 海上輸出ブッキング参照' },
  'Job No / Booking / Vessel / POL / Dest Port / Cargo…': { zh: '工作号 / 订舱号 / 船名 / 起运港 / 目的港 / 货物…', ja: 'ジョブ番号 / ブッキング / 船名 / POL / 仕向港 / 貨物…' },
  'Send Bkg Con': { zh: '发送订舱确认', ja: 'ブッキング確認送信' },
  'Draft Booking Email': { zh: '生成订舱邮件草稿', ja: 'ブッキングメール草稿' },
  'Generate Booking Note': { zh: '生成订舱备注', ja: 'ブッキングノート生成' },
  'Invoice / Cost Note': { zh: '发票 / 成本单', ja: '請求 / 原価ノート' },
  'CMS PRO2 finance document header read-only browser': { zh: 'CMS PRO2 财务单据抬头只读浏览', ja: 'CMS PRO2 財務伝票ヘッダー参照' },
  'Filter this page: Doc No / Company / Job File Id…': { zh: '筛选本页：单号 / 公司 / 工作档ID…', ja: 'このページを絞込: 伝票番号 / 会社 / ジョブファイルID…' },
  'Charge Lines': { zh: '费用明细', ja: '料金明細' },
  'No charge lines found for this document.': { zh: '此单据没有费用明细。', ja: 'この伝票に料金明細はありません。' },
  'Cost Note Lines': { zh: '成本单明细', ja: '原価ノート明細' },
  'CMS PRO2 finance charge-line read-only browser': { zh: 'CMS PRO2 财务费用明细只读浏览', ja: 'CMS PRO2 財務チャージ明細参照' },
  'Filter this page: Doc No / Charge Code / Charge Name / Job File Id…': { zh: '筛选本页：单号 / 费用代码 / 费用名称 / 工作档ID…', ja: 'このページを絞込: 伝票番号 / チャージコード / チャージ名 / ジョブファイルID…' },
  'Invoice FX': { zh: '发票汇率', ja: '請求為替' },
  'CMS PRO2 invoice FX read-only browser': { zh: 'CMS PRO2 发票汇率只读浏览', ja: 'CMS PRO2 請求為替参照' },
  'Filter this page: Inv No / Currency / Job File Id…': { zh: '筛选本页：单号 / 币别 / 工作档ID…', ja: 'このページを絞込: 伝票番号 / 通貨 / ジョブファイルID…' },
  Companies: { zh: '公司资料', ja: '会社マスタ' },
  'CMS PRO2 company master read-only browser': { zh: 'CMS PRO2 公司主数据只读浏览', ja: 'CMS PRO2 会社マスタ参照' },
  'Server search: company name…': { zh: '服务器搜索：公司名称…', ja: 'サーバ検索: 会社名…' },
  'Recent Inv / Cost Note': { zh: '最近发票 / 成本单', ja: '最近の請求 / 原価ノート' },
  'No finance documents found for this company.': { zh: '该公司没有财务单据。', ja: 'この会社の財務伝票はありません。' },
  'Recent Export Bookings': { zh: '最近出口订舱', ja: '最近の輸出ブッキング' },
  'No export bookings found for this company.': { zh: '该公司没有出口订舱。', ja: 'この会社の輸出ブッキングはありません。' },
};

function pro2Text(text: string): string {
  const lang = i18next.language?.startsWith('zh') ? 'zh' : i18next.language?.startsWith('ja') ? 'ja' : 'en';
  if (lang === 'en') return text;
  return PRO2_I18N[text]?.[lang] ?? text;
}

function translatePro2Columns(columns: NonNullable<ViewTemplateDSL['columns']>): NonNullable<ViewTemplateDSL['columns']> {
  return columns.map((column) => ({ ...column, label: column.label ? pro2Text(column.label) : column.label }));
}

function translatePro2Sections(sections: NonNullable<ViewTemplateDSL['sections']>): NonNullable<ViewTemplateDSL['sections']> {
  return sections.map((section) => ({
    ...section,
    title: pro2Text(section.title),
    fields: section.fields.map((field) => ({ ...field, label: field.label ? pro2Text(field.label) : field.label })),
  }));
}

// ─── FMS Sea Import per-tab columns ─────────────────────────────────────────

const FMS_SEA_IMPORT_COMMON = [
  { field: 'shipmentNo',    label: 'Job No',   width: 120, sortable: true,  pinned: 'left' as const },
  { field: 'level',        label: 'M/H',      width: 55,  format: 'status' as const, sortable: true },
  { field: 'status',       label: 'Status',   width: 90,  format: 'status' as const, sortable: true },
  { field: 'customer_name', label: 'Customer', width: 130 },
  { field: 'carrier_name', label: 'Carrier',  width: 110 },
  { field: 'masterBlNo',   label: 'MBL No',   width: 140, sortable: true },
  { field: 'houseBlNo',    label: 'HBL No',   width: 140, sortable: true },
  { field: 'vessel',       label: 'Vessel',   width: 120, sortable: true },
  { field: 'voyage',       label: 'Voy',      width: 60 },
  { field: 'pol',          label: 'POL',      width: 65,  sortable: true },
  { field: 'pod',          label: 'POD',      width: 65,  sortable: true },
  { field: 'etd',          label: 'ETD',      width: 90,  format: 'date' as const, sortable: true },
  { field: 'eta',          label: 'ETA',      width: 90,  format: 'date' as const, sortable: true },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const FMS_SEA_IMPORT_FCL_COLUMNS = [
  ...FMS_SEA_IMPORT_COMMON,
  { field: 'container_numbers', label: 'Containers', width: 140 },
  { field: 'totalPackages', label: 'Pkgs',    width: 55, sortable: true },
  { field: 'totalWeight',  label: 'Weight',  width: 70, sortable: true },
  { field: 'totalVolume',  label: 'CBM',     width: 60, sortable: true },
  { field: 'freightTerm',  label: 'Freight', width: 70, format: 'status' as const, sortable: true },
  { field: 'daysToEta',    label: 'Tracking', width: 80 },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const FMS_SEA_IMPORT_LCL_COLUMNS = [
  ...FMS_SEA_IMPORT_COMMON,
  { field: 'container_numbers', label: 'Containers', width: 140 },
  { field: 'totalPackages', label: 'Pkgs',    width: 55, sortable: true },
  { field: 'totalWeight',  label: 'Weight',  width: 70, sortable: true },
  { field: 'totalVolume',  label: 'CBM',     width: 60, sortable: true },
  { field: 'freightTerm',  label: 'Freight', width: 70, format: 'status' as const, sortable: true },
  { field: 'daysToEta',    label: 'Tracking', width: 80 },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const FMS_SEA_IMPORT_CONSOL_COLUMNS = [
  ...FMS_SEA_IMPORT_COMMON,
  { field: 'totalPackages', label: 'Pkgs',    width: 55, sortable: true },
  { field: 'totalWeight',  label: 'Weight',  width: 70, sortable: true },
  { field: 'totalVolume',  label: 'CBM',     width: 60, sortable: true },
  { field: 'freightTerm',  label: 'Freight', width: 70, format: 'status' as const, sortable: true },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const FMS_SEA_EXPORT_FCL_COLUMNS = [
  ...FMS_SEA_IMPORT_COMMON,
  { field: 'container_numbers', label: 'Containers', width: 140 },
  { field: 'totalPackages', label: 'Pkgs',    width: 55, sortable: true },
  { field: 'totalWeight',  label: 'Weight',  width: 70, sortable: true },
  { field: 'totalVolume',  label: 'CBM',     width: 60, sortable: true },
  { field: 'freightTerm',  label: 'Freight', width: 70, format: 'status' as const, sortable: true },
  { field: 'daysToEta',    label: 'Tracking', width: 80 },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const FMS_SEA_EXPORT_LCL_COLUMNS = [
  ...FMS_SEA_IMPORT_COMMON,
  { field: 'container_numbers', label: 'Containers', width: 140 },
  { field: 'totalPackages', label: 'Pkgs',    width: 55, sortable: true },
  { field: 'totalWeight',  label: 'Weight',  width: 70, sortable: true },
  { field: 'totalVolume',  label: 'CBM',     width: 60, sortable: true },
  { field: 'freightTerm',  label: 'Freight', width: 70, format: 'status' as const, sortable: true },
  { field: 'daysToEta',    label: 'Tracking', width: 80 },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const FMS_SEA_EXPORT_CONSOL_COLUMNS = [
  ...FMS_SEA_IMPORT_COMMON,
  { field: 'totalPackages', label: 'Pkgs',    width: 55, sortable: true },
  { field: 'totalWeight',  label: 'Weight',  width: 70, sortable: true },
  { field: 'totalVolume',  label: 'CBM',     width: 60, sortable: true },
  { field: 'freightTerm',  label: 'Freight', width: 70, format: 'status' as const, sortable: true },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const FMS_AIR_IMPORT_COLUMNS = [
  { field: 'shipmentNo', label: 'Job No', width: 120, sortable: true, pinned: 'left' as const },
  { field: 'status', label: 'Status', width: 100, format: 'status' as const, sortable: true },
  { field: 'customer_name', label: 'Consignee / Customer', width: 180 },
  { field: 'carrier_name', label: 'Airline', width: 150 },
  { field: 'masterBlNo', label: 'MAWB', width: 150, sortable: true },
  { field: 'houseBlNo', label: 'HAWB', width: 150, sortable: true },
  { field: 'vessel', label: 'Flight', width: 120, sortable: true },
  { field: 'voyage', label: 'Flight Ref', width: 110 },
  { field: 'pol', label: 'Origin', width: 90, sortable: true },
  { field: 'pod', label: 'Arrival', width: 90, sortable: true },
  { field: 'eta', label: 'ETA', width: 140, format: 'datetime' as const, sortable: true },
  { field: 'totalPackages', label: 'Pcs', width: 70, format: 'number' as const, sortable: true },
  { field: 'totalWeight', label: 'GW kg', width: 90, format: 'number' as const, sortable: true },
  { field: 'freightTerm', label: 'Freight', width: 90, format: 'status' as const, sortable: true },
  { field: 'updatedAt', label: 'Last Update', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const FMS_AIR_EXPORT_COLUMNS = [
  { field: 'shipmentNo', label: 'Job No', width: 120, sortable: true, pinned: 'left' as const },
  { field: 'status', label: 'Status', width: 100, format: 'status' as const, sortable: true },
  { field: 'customer_name', label: 'Shipper / Customer', width: 180 },
  { field: 'carrier_name', label: 'Airline / GSA', width: 150 },
  { field: 'houseBlNo', label: 'HAWB', width: 150, sortable: true },
  { field: 'masterBlNo', label: 'MAWB', width: 150, sortable: true },
  { field: 'vessel', label: 'Flight', width: 120, sortable: true },
  { field: 'voyage', label: 'Flight Ref', width: 110 },
  { field: 'pol', label: 'Origin', width: 90, sortable: true },
  { field: 'pod', label: 'Destination', width: 90, sortable: true },
  { field: 'etd', label: 'ETD', width: 140, format: 'datetime' as const, sortable: true },
  { field: 'blNumber', label: 'Booking Ref', width: 140 },
  { field: 'totalPackages', label: 'Pcs', width: 70, format: 'number' as const, sortable: true },
  { field: 'totalWeight', label: 'GW kg', width: 90, format: 'number' as const, sortable: true },
  { field: 'freightTerm', label: 'Freight', width: 90, format: 'status' as const, sortable: true },
  { field: 'updatedAt', label: 'Last Update', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

// ─── Generic Shipments list (admin / all directions) ─────────────────────────

const SHIPMENT_LIST_COLUMNS = [
  { field: 'job_number', label: 'Job No', width: 120, pinned: 'left' as const },
  { field: 'shipmentNo', label: 'Shipment No', width: 130, pinned: 'left' as const },
  { field: 'shipmentType', label: 'Type', width: 90, format: 'status' as const },
  { field: 'level', label: 'Level', width: 90, format: 'status' as const, editable: true },
  { field: 'status', label: 'Status', width: 110, format: 'status' as const, editable: true },
  { field: 'customer_name', label: 'Customer', width: 160 },
  { field: 'carrier_name', label: 'Carrier', width: 140 },
  { field: 'masterBlNo', label: 'MBL', width: 160, editable: true },
  { field: 'houseBlNo', label: 'HBL', width: 160, editable: true },
  { field: 'container_numbers', label: 'Container No', width: 170 },
  { field: 'vessel', label: 'Vessel', width: 150, editable: true },
  { field: 'voyage', label: 'Voyage', width: 120, editable: true },
  { field: 'pol', label: 'POL', width: 90, editable: true },
  { field: 'pod', label: 'POD', width: 90, editable: true },
  { field: 'etd', label: 'ETD', width: 130, format: 'datetime' as const, editable: true },
  { field: 'eta', label: 'ETA', width: 130, format: 'datetime' as const, editable: true },
  { field: 'totalPackages', label: 'Packages', width: 110, format: 'number' as const, editable: true },
  { field: 'totalWeight', label: 'Weight', width: 120, format: 'number' as const, editable: true },
  { field: 'totalVolume', label: 'Volume', width: 120, format: 'number' as const, editable: true },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' as const, pinned: 'right' as const },
  { field: 'updatedAt', label: 'Modified', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const SHIPMENT_DETAIL_SECTIONS = [
  {
    title: 'Identity',
    columns: 6 as const,
    fields: [
      { field: 'job_number', label: 'Job No' },
      { field: 'shipmentNo', label: 'Shipment No' },
      { field: 'shipmentType', label: 'Shipment Type', format: 'status' },
      { field: 'level', label: 'Level', format: 'status' },
      { field: 'status', label: 'Status', format: 'status' },
      { field: 'loadType', label: 'Load Type', format: 'status' },
      { field: 'masterBlNo', label: 'Master BL' },
      { field: 'houseBlNo', label: 'House BL' },
      { field: 'blNumber', label: 'BL Number' },
      { field: 'customer_name', label: 'Customer' },
      { field: 'carrier_name', label: 'Carrier' },
      { field: 'freightTerm', label: 'Freight Term' },
    ],
  },
  {
    title: 'Routing',
    columns: 6 as const,
    fields: [
      { field: 'direction', label: 'Direction', format: 'status' },
      { field: 'mode', label: 'Mode', format: 'status' },
      { field: 'pol', label: 'POL' },
      { field: 'pod', label: 'POD' },
      { field: 'vessel', label: 'Vessel' },
      { field: 'voyage', label: 'Voyage' },
      { field: 'etd', label: 'ETD', format: 'datetime' },
      { field: 'eta', label: 'ETA', format: 'datetime' },
      { field: 'shipper_name', label: 'Shipper' },
      { field: 'consignee_name', label: 'Consignee' },
      { field: 'overseas_agent_name', label: 'Overseas Agent' },
      { field: 'data', label: 'Source Packet' },
    ],
  },
  {
    title: 'Totals',
    columns: 6 as const,
    fields: [
      { field: 'totalPackages', label: 'Packages', format: 'number' },
      { field: 'totalWeight', label: 'Weight', format: 'number' },
      { field: 'totalVolume', label: 'Volume', format: 'number' },
      { field: 'totalRevenue', label: 'Revenue', format: 'currency' },
      { field: 'totalCost', label: 'Cost', format: 'currency' },
      { field: 'createdAt', label: 'Created', format: 'datetime' },
      { field: 'updatedAt', label: 'Modified', format: 'datetime' },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;
// ── FMS Sea Import — direction-aware detail sections ────────────────────────
const FMS_SEA_IMPORT_DETAIL_SECTIONS = [
  {
    title: 'Import Shipment',
    columns: 6 as const,
    fields: [
      { field: 'job_number',          label: 'Job No' },
      { field: 'shipmentNo',          label: 'Shipment No' },
      { field: 'masterBlNo',          label: 'Master BL' },
      { field: 'houseBlNo',           label: 'House BL' },
      { field: 'status',              label: 'Status',      format: 'status' as const },
      { field: 'loadType',            label: 'Load Type',   format: 'status' as const },
      { field: 'customer_name',       label: 'Customer' },
      { field: 'carrier_name',        label: 'Carrier' },
      { field: 'consignee_name',      label: 'Consignee' },
      { field: 'shipper_name',        label: 'Shipper' },
      { field: 'overseas_agent_name', label: 'Overseas Agent' },
      { field: 'freightTerm',         label: 'Freight Term' },
    ],
  },
  {
    title: 'Arrival & Routing',
    columns: 6 as const,
    fields: [
      { field: 'pol',                   label: 'POL' },
      { field: 'pod',                   label: 'POD' },
      { field: 'vessel',                label: 'Vessel' },
      { field: 'voyage',                label: 'Voyage' },
      { field: 'eta',                   label: 'ETA',                    format: 'datetime' as const },
      { field: 'etd',                   label: 'ETD',                    format: 'datetime' as const },
      { field: 'arrival_date',          label: 'Arrival Date',           format: 'datetime' as const },
      { field: 'berth',                 label: 'Berth / Terminal' },
      { field: 'customsClearanceDate',  label: 'Customs Clearance Date', format: 'datetime' as const },
      { field: 'deliveryDate',          label: 'Last Day of Delivery',   format: 'datetime' as const },
      { field: 'containerReturnDate',   label: 'Container Return Date',  format: 'datetime' as const },
      { field: 'portClosingDate',       label: 'Port Closing Date',      format: 'datetime' as const },
    ],
  },
  {
    title: 'Cargo & Totals',
    columns: 6 as const,
    fields: [
      { field: 'totalPackages', label: 'Packages', format: 'number' as const },
      { field: 'totalWeight',   label: 'Weight',   format: 'number' as const },
      { field: 'totalVolume',   label: 'Volume',   format: 'number' as const },
      { field: 'totalRevenue',  label: 'Revenue',  format: 'currency' as const },
      { field: 'totalCost',     label: 'Cost',     format: 'currency' as const },
      { field: 'data',          label: 'Source Packet' },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

// ── FMS Sea Export — direction-aware detail sections ────────────────────────
const FMS_SEA_EXPORT_DETAIL_SECTIONS = [
  {
    title: 'Export Shipment',
    columns: 6 as const,
    fields: [
      { field: 'job_number',          label: 'Job No' },
      { field: 'shipmentNo',          label: 'Shipment No' },
      { field: 'masterBlNo',          label: 'Master BL' },
      { field: 'houseBlNo',           label: 'House BL' },
      { field: 'status',              label: 'Status',    format: 'status' as const },
      { field: 'loadType',            label: 'Load Type', format: 'status' as const },
      { field: 'customer_name',       label: 'Customer' },
      { field: 'carrier_name',        label: 'Carrier' },
      { field: 'shipper_name',        label: 'Shipper' },
      { field: 'consignee_name',      label: 'Consignee' },
      { field: 'overseas_agent_name', label: 'Overseas Agent' },
      { field: 'freightTerm',         label: 'Freight Term' },
    ],
  },
  {
    title: 'Departure & Cut-offs',
    columns: 6 as const,
    fields: [
      { field: 'pol',            label: 'POL' },
      { field: 'pod',            label: 'POD' },
      { field: 'vessel',         label: 'Vessel' },
      { field: 'voyage',         label: 'Voyage' },
      { field: 'etd',            label: 'ETD',             format: 'datetime' as const },
      { field: 'eta',            label: 'ETA',             format: 'datetime' as const },
      { field: 'bookingRef',     label: 'Booking Ref' },
      { field: 'blNumber',       label: 'BL Number' },
      { field: 'siCutoff',       label: 'SI Cut-off',      format: 'datetime' as const },
      { field: 'vgmCutoff',      label: 'VGM Cut-off',     format: 'datetime' as const },
      { field: 'gateInCutoff',   label: 'Gate-in Cut-off', format: 'datetime' as const },
      { field: 'portClosingDate',label: 'Port Closing',    format: 'datetime' as const },
    ],
  },
  {
    title: 'Cargo & Totals',
    columns: 6 as const,
    fields: [
      { field: 'totalPackages', label: 'Packages', format: 'number' as const },
      { field: 'totalWeight',   label: 'Weight',   format: 'number' as const },
      { field: 'totalVolume',   label: 'Volume',   format: 'number' as const },
      { field: 'totalRevenue',  label: 'Revenue',  format: 'currency' as const },
      { field: 'totalCost',     label: 'Cost',     format: 'currency' as const },
      { field: 'data',          label: 'Source Packet' },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const FMS_AIR_IMPORT_DETAIL_SECTIONS = [
  {
    title: 'MAWB / Arrival File',
    columns: 6 as const,
    fields: [
      { field: 'job_number', label: 'Job No' },
      { field: 'shipmentNo', label: 'Shipment No' },
      { field: 'masterBlNo', label: 'MAWB' },
      { field: 'houseBlNo', label: 'HAWB' },
      { field: 'status', label: 'Status', format: 'status' as const },
      { field: 'freightTerm', label: 'Freight Term' },
      { field: 'customer_name', label: 'Customer' },
      { field: 'consignee_name', label: 'Consignee' },
      { field: 'shipper_name', label: 'Shipper' },
      { field: 'carrier_name', label: 'Airline' },
      { field: 'overseas_agent_name', label: 'Origin Agent' },
      { field: 'loadType', label: 'Handling Type', format: 'status' as const },
    ],
  },
  {
    title: 'Flight & Clearance',
    columns: 6 as const,
    fields: [
      { field: 'vessel', label: 'Flight' },
      { field: 'voyage', label: 'Flight Ref' },
      { field: 'pol', label: 'Origin Airport' },
      { field: 'pod', label: 'Arrival Airport' },
      { field: 'etd', label: 'ETD', format: 'datetime' as const },
      { field: 'eta', label: 'ETA', format: 'datetime' as const },
      { field: 'placeOfReceipt', label: 'Place of Receipt' },
      { field: 'finalDestination', label: 'Final Delivery' },
      { field: 'blNumber', label: 'Import Ref' },
      { field: 'currency', label: 'Currency' },
      { field: 'createdAt', label: 'Created', format: 'datetime' as const },
      { field: 'updatedAt', label: 'Updated', format: 'datetime' as const },
    ],
  },
  {
    title: 'Cargo & Finance',
    columns: 6 as const,
    fields: [
      { field: 'totalPackages', label: 'Pieces', format: 'number' as const },
      { field: 'totalWeight', label: 'Gross Weight', format: 'number' as const },
      { field: 'totalVolume', label: 'Volume', format: 'number' as const },
      { field: 'totalRevenue', label: 'Revenue', format: 'currency' as const },
      { field: 'totalCost', label: 'Cost', format: 'currency' as const },
      { field: 'notes', label: 'Ops Notes' },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const FMS_AIR_EXPORT_DETAIL_SECTIONS = [
  {
    title: 'Booking & MAWB Control',
    columns: 6 as const,
    fields: [
      { field: 'job_number', label: 'Job No' },
      { field: 'shipmentNo', label: 'Shipment No' },
      { field: 'houseBlNo', label: 'HAWB' },
      { field: 'masterBlNo', label: 'MAWB' },
      { field: 'blNumber', label: 'Booking Ref' },
      { field: 'status', label: 'Status', format: 'status' as const },
      { field: 'customer_name', label: 'Customer' },
      { field: 'shipper_name', label: 'Shipper' },
      { field: 'consignee_name', label: 'Consignee' },
      { field: 'carrier_name', label: 'Airline / GSA' },
      { field: 'overseas_agent_name', label: 'Destination Agent' },
      { field: 'freightTerm', label: 'Freight Term' },
    ],
  },
  {
    title: 'Flight & Cut-off',
    columns: 6 as const,
    fields: [
      { field: 'vessel', label: 'Flight' },
      { field: 'voyage', label: 'Flight Ref' },
      { field: 'pol', label: 'Export Airport' },
      { field: 'pod', label: 'Destination Airport' },
      { field: 'etd', label: 'ETD', format: 'datetime' as const },
      { field: 'eta', label: 'ETA', format: 'datetime' as const },
      { field: 'placeOfReceipt', label: 'Cargo Ready At' },
      { field: 'finalDestination', label: 'Final Destination' },
      { field: 'incoTerm', label: 'Inco Term' },
      { field: 'currency', label: 'Currency' },
      { field: 'createdAt', label: 'Created', format: 'datetime' as const },
      { field: 'updatedAt', label: 'Updated', format: 'datetime' as const },
    ],
  },
  {
    title: 'Cargo & Finance',
    columns: 6 as const,
    fields: [
      { field: 'totalPackages', label: 'Pieces', format: 'number' as const },
      { field: 'totalWeight', label: 'Gross Weight', format: 'number' as const },
      { field: 'totalVolume', label: 'Volume', format: 'number' as const },
      { field: 'totalRevenue', label: 'Revenue', format: 'currency' as const },
      { field: 'totalCost', label: 'Cost', format: 'currency' as const },
      { field: 'notes', label: 'Ops Notes' },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

// ─── FMS Classic templates (used by getClassicTemplate when designMode='classic') ─────
// Flat, compact, no assist panel, no detail dock, all columns visible, lineMode: '1-line'.
// One shared Sea list, one Air list; four direction-specific detail flats.

const FMS_SEA_CLASSIC_LIST_TEMPLATE: ViewTemplateDSL = {
  columns: [
    { field: 'shipmentNo',        label: 'Job No',    width: 120, sortable: true, pinned: 'left' as const },
    { field: 'level',             label: 'M/H',       width: 55,  format: 'status' as const },
    { field: 'status',            label: 'Status',    width: 90,  format: 'status' as const },
    { field: 'customer_name',     label: 'Customer',  width: 130 },
    { field: 'carrier_name',      label: 'Carrier',   width: 110 },
    { field: 'masterBlNo',        label: 'MBL No',    width: 140, sortable: true },
    { field: 'houseBlNo',         label: 'HBL No',    width: 140, sortable: true },
    { field: 'vessel',            label: 'Vessel',    width: 120, sortable: true },
    { field: 'voyage',            label: 'Voy',       width: 60 },
    { field: 'pol',               label: 'POL',       width: 65,  sortable: true },
    { field: 'pod',               label: 'POD',       width: 65,  sortable: true },
    { field: 'etd',               label: 'ETD',       width: 90,  format: 'date' as const, sortable: true },
    { field: 'eta',               label: 'ETA',       width: 90,  format: 'date' as const, sortable: true },
    { field: 'container_numbers', label: 'Containers',width: 150 },
    { field: 'totalPackages',     label: 'Pkgs',      width: 60,  sortable: true },
    { field: 'totalWeight',       label: 'Weight',    width: 80,  sortable: true },
    { field: 'totalVolume',       label: 'CBM',       width: 70,  sortable: true },
    { field: 'freightTerm',       label: 'Freight',   width: 75,  format: 'status' as const },
    { field: 'updatedAt',         label: 'Modified',  width: 130, format: 'datetime' as const, pinned: 'right' as const },
  ],
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'compact-first' },
  list: { kind: 'major-grid', lineMode: '1-line', sorting: 'server', paging: 'server' },
};

const FMS_AIR_CLASSIC_LIST_TEMPLATE: ViewTemplateDSL = {
  columns: [
    { field: 'shipmentNo',    label: 'Job No',       width: 120, sortable: true, pinned: 'left' as const },
    { field: 'status',        label: 'Status',       width: 100, format: 'status' as const, sortable: true },
    { field: 'customer_name', label: 'Customer',     width: 160 },
    { field: 'carrier_name',  label: 'Airline',      width: 140 },
    { field: 'masterBlNo',    label: 'MAWB',         width: 150, sortable: true },
    { field: 'houseBlNo',     label: 'HAWB',         width: 150, sortable: true },
    { field: 'vessel',        label: 'Flight',       width: 120, sortable: true },
    { field: 'voyage',        label: 'Flight Ref',   width: 110 },
    { field: 'pol',           label: 'Origin',       width: 90,  sortable: true },
    { field: 'pod',           label: 'Destination',  width: 90,  sortable: true },
    { field: 'etd',           label: 'ETD',          width: 130, format: 'datetime' as const, sortable: true },
    { field: 'eta',           label: 'ETA',          width: 130, format: 'datetime' as const, sortable: true },
    { field: 'totalPackages', label: 'Pcs',          width: 70,  format: 'number' as const, sortable: true },
    { field: 'totalWeight',   label: 'GW kg',        width: 90,  format: 'number' as const, sortable: true },
    { field: 'freightTerm',   label: 'Freight',      width: 90,  format: 'status' as const },
    { field: 'updatedAt',     label: 'Last Update',  width: 130, format: 'datetime' as const, pinned: 'right' as const },
  ],
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'compact-first' },
  list: { kind: 'major-grid', lineMode: '1-line', sorting: 'server', paging: 'server' },
};

const FMS_SEA_IMPORT_DETAIL_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'Import Shipment',
      columns: 2 as const,
      fields: [
        { field: 'job_number',          label: 'Job No' },
        { field: 'shipmentNo',          label: 'Shipment No' },
        { field: 'masterBlNo',          label: 'Master BL' },
        { field: 'houseBlNo',           label: 'House BL' },
        { field: 'status',              label: 'Status',      format: 'status' as const },
        { field: 'loadType',            label: 'Load Type',   format: 'status' as const },
        { field: 'customer_name',       label: 'Customer' },
        { field: 'carrier_name',        label: 'Carrier' },
        { field: 'consignee_name',      label: 'Consignee' },
        { field: 'shipper_name',        label: 'Shipper' },
        { field: 'overseas_agent_name', label: 'Overseas Agent' },
        { field: 'freightTerm',         label: 'Freight Term' },
      ],
    },
    {
      title: 'Arrival & Routing',
      columns: 2 as const,
      fields: [
        { field: 'pol',                  label: 'POL' },
        { field: 'pod',                  label: 'POD' },
        { field: 'vessel',               label: 'Vessel' },
        { field: 'voyage',               label: 'Voyage' },
        { field: 'eta',                  label: 'ETA',                    format: 'datetime' as const },
        { field: 'etd',                  label: 'ETD',                    format: 'datetime' as const },
        { field: 'arrival_date',         label: 'Arrival Date',           format: 'datetime' as const },
        { field: 'customsClearanceDate', label: 'Customs Clearance Date', format: 'datetime' as const },
        { field: 'deliveryDate',         label: 'Last Day of Delivery',   format: 'datetime' as const },
        { field: 'containerReturnDate',  label: 'Container Return Date',  format: 'datetime' as const },
      ],
    },
    {
      title: 'Cargo & Totals',
      columns: 2 as const,
      fields: [
        { field: 'totalPackages', label: 'Packages', format: 'number' as const },
        { field: 'totalWeight',   label: 'Weight',   format: 'number' as const },
        { field: 'totalVolume',   label: 'Volume',   format: 'number' as const },
        { field: 'totalRevenue',  label: 'Revenue',  format: 'currency' as const },
        { field: 'totalCost',     label: 'Cost',     format: 'currency' as const },
      ],
    },
  ],
  density: 'compact',
  layout: { desktopColumns: 2, mobileColumns: 2, pageMode: 'record', surface: 'compact-first' },
};

const FMS_SEA_EXPORT_DETAIL_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'Export Shipment',
      columns: 2 as const,
      fields: [
        { field: 'job_number',          label: 'Job No' },
        { field: 'shipmentNo',          label: 'Shipment No' },
        { field: 'masterBlNo',          label: 'Master BL' },
        { field: 'houseBlNo',           label: 'House BL' },
        { field: 'status',              label: 'Status',    format: 'status' as const },
        { field: 'loadType',            label: 'Load Type', format: 'status' as const },
        { field: 'customer_name',       label: 'Customer' },
        { field: 'carrier_name',        label: 'Carrier' },
        { field: 'shipper_name',        label: 'Shipper' },
        { field: 'consignee_name',      label: 'Consignee' },
        { field: 'overseas_agent_name', label: 'Overseas Agent' },
        { field: 'freightTerm',         label: 'Freight Term' },
      ],
    },
    {
      title: 'Departure & Cut-offs',
      columns: 2 as const,
      fields: [
        { field: 'pol',            label: 'POL' },
        { field: 'pod',            label: 'POD' },
        { field: 'vessel',         label: 'Vessel' },
        { field: 'voyage',         label: 'Voyage' },
        { field: 'etd',            label: 'ETD',             format: 'datetime' as const },
        { field: 'eta',            label: 'ETA',             format: 'datetime' as const },
        { field: 'bookingRef',     label: 'Booking Ref' },
        { field: 'blNumber',       label: 'BL Number' },
        { field: 'siCutoff',       label: 'SI Cut-off',      format: 'datetime' as const },
        { field: 'vgmCutoff',      label: 'VGM Cut-off',     format: 'datetime' as const },
        { field: 'gateInCutoff',   label: 'Gate-in Cut-off', format: 'datetime' as const },
        { field: 'portClosingDate',label: 'Port Closing',    format: 'datetime' as const },
      ],
    },
    {
      title: 'Cargo & Totals',
      columns: 2 as const,
      fields: [
        { field: 'totalPackages', label: 'Packages', format: 'number' as const },
        { field: 'totalWeight',   label: 'Weight',   format: 'number' as const },
        { field: 'totalVolume',   label: 'Volume',   format: 'number' as const },
        { field: 'totalRevenue',  label: 'Revenue',  format: 'currency' as const },
        { field: 'totalCost',     label: 'Cost',     format: 'currency' as const },
      ],
    },
  ],
  density: 'compact',
  layout: { desktopColumns: 2, mobileColumns: 2, pageMode: 'record', surface: 'compact-first' },
};

const FMS_AIR_IMPORT_DETAIL_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'MAWB / Arrival File',
      columns: 2 as const,
      fields: [
        { field: 'job_number',          label: 'Job No' },
        { field: 'shipmentNo',          label: 'Shipment No' },
        { field: 'masterBlNo',          label: 'MAWB' },
        { field: 'houseBlNo',           label: 'HAWB' },
        { field: 'status',              label: 'Status',          format: 'status' as const },
        { field: 'freightTerm',         label: 'Freight Term' },
        { field: 'customer_name',       label: 'Customer' },
        { field: 'consignee_name',      label: 'Consignee' },
        { field: 'shipper_name',        label: 'Shipper' },
        { field: 'carrier_name',        label: 'Airline' },
        { field: 'overseas_agent_name', label: 'Origin Agent' },
        { field: 'loadType',            label: 'Handling Type',   format: 'status' as const },
      ],
    },
    {
      title: 'Flight & Clearance',
      columns: 2 as const,
      fields: [
        { field: 'vessel',           label: 'Flight' },
        { field: 'voyage',           label: 'Flight Ref' },
        { field: 'pol',              label: 'Origin Airport' },
        { field: 'pod',              label: 'Arrival Airport' },
        { field: 'etd',              label: 'ETD', format: 'datetime' as const },
        { field: 'eta',              label: 'ETA', format: 'datetime' as const },
        { field: 'placeOfReceipt',   label: 'Place of Receipt' },
        { field: 'finalDestination', label: 'Final Delivery' },
        { field: 'totalPackages',    label: 'Pieces',      format: 'number' as const },
        { field: 'totalWeight',      label: 'Gross Weight',format: 'number' as const },
        { field: 'totalRevenue',     label: 'Revenue',     format: 'currency' as const },
        { field: 'totalCost',        label: 'Cost',        format: 'currency' as const },
      ],
    },
  ],
  density: 'compact',
  layout: { desktopColumns: 2, mobileColumns: 2, pageMode: 'record', surface: 'compact-first' },
};

const FMS_AIR_EXPORT_DETAIL_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'Booking & MAWB Control',
      columns: 2 as const,
      fields: [
        { field: 'job_number',          label: 'Job No' },
        { field: 'shipmentNo',          label: 'Shipment No' },
        { field: 'houseBlNo',           label: 'HAWB' },
        { field: 'masterBlNo',          label: 'MAWB' },
        { field: 'blNumber',            label: 'Booking Ref' },
        { field: 'status',              label: 'Status',             format: 'status' as const },
        { field: 'customer_name',       label: 'Customer' },
        { field: 'shipper_name',        label: 'Shipper' },
        { field: 'consignee_name',      label: 'Consignee' },
        { field: 'carrier_name',        label: 'Airline / GSA' },
        { field: 'overseas_agent_name', label: 'Destination Agent' },
        { field: 'freightTerm',         label: 'Freight Term' },
      ],
    },
    {
      title: 'Flight & Cargo',
      columns: 2 as const,
      fields: [
        { field: 'vessel',           label: 'Flight' },
        { field: 'voyage',           label: 'Flight Ref' },
        { field: 'pol',              label: 'Export Airport' },
        { field: 'pod',              label: 'Destination Airport' },
        { field: 'etd',              label: 'ETD',          format: 'datetime' as const },
        { field: 'eta',              label: 'ETA',          format: 'datetime' as const },
        { field: 'placeOfReceipt',   label: 'Cargo Ready At' },
        { field: 'finalDestination', label: 'Final Destination' },
        { field: 'totalPackages',    label: 'Pieces',       format: 'number' as const },
        { field: 'totalWeight',      label: 'Gross Weight', format: 'number' as const },
        { field: 'totalRevenue',     label: 'Revenue',      format: 'currency' as const },
        { field: 'totalCost',        label: 'Cost',         format: 'currency' as const },
      ],
    },
  ],
  density: 'compact',
  layout: { desktopColumns: 2, mobileColumns: 2, pageMode: 'record', surface: 'compact-first' },
};

const FMS_SEA_IMPORT_FORM_FIELDS = [
  { field: 'shipmentNo', label: 'Job No', group: 'Identity' },
  { field: 'shipmentType', label: 'Shipment Type', group: 'Identity' },
  { field: 'level', label: 'Master / House', group: 'Identity' },
  { field: 'status', label: 'Status', group: 'Identity' },
  { field: 'direction', label: 'Direction', group: 'Identity' },
  { field: 'mode', label: 'Mode', group: 'Identity' },
  { field: 'loadType', label: 'Load Type', group: 'Shipment Setup' },
  { field: 'customerId', label: 'Customer', group: 'Shipment Setup' },
  { field: 'carrierId', label: 'Carrier', group: 'Shipment Setup' },
  { field: 'overseasAgentId', label: 'Origin Agent', group: 'Shipment Setup' },
  { field: 'pol', label: 'POL', group: 'Routing' },
  { field: 'pod', label: 'POD', group: 'Routing' },
  { field: 'vessel', label: 'Vessel', group: 'Routing' },
  { field: 'voyage', label: 'Voyage', group: 'Routing' },
  { field: 'etd', label: 'ETD', group: 'Routing' },
  { field: 'eta', label: 'ETA', group: 'Routing' },
  { field: 'masterBlNo', label: 'MBL', group: 'Documents' },
  { field: 'houseBlNo', label: 'HBL', group: 'Documents' },
  { field: 'blNumber', label: 'BL Ref', group: 'Documents' },
  { field: 'freightTerm', label: 'Freight Term', group: 'Documents' },
  { field: 'totalPackages', label: 'Packages', group: 'Cargo' },
  { field: 'totalWeight', label: 'Gross Weight', group: 'Cargo' },
  { field: 'totalVolume', label: 'Volume', group: 'Cargo' },
  { field: 'notes', label: 'Ops Notes', group: 'Cargo', span: 2 as const },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const FMS_SEA_EXPORT_FORM_FIELDS = [
  { field: 'shipmentNo', label: 'Job No', group: 'Identity' },
  { field: 'shipmentType', label: 'Shipment Type', group: 'Identity' },
  { field: 'level', label: 'Master / House', group: 'Identity' },
  { field: 'status', label: 'Status', group: 'Identity' },
  { field: 'direction', label: 'Direction', group: 'Identity' },
  { field: 'mode', label: 'Mode', group: 'Identity' },
  { field: 'loadType', label: 'Load Type', group: 'Booking' },
  { field: 'customerId', label: 'Customer', group: 'Booking' },
  { field: 'carrierId', label: 'Carrier', group: 'Booking' },
  { field: 'shipperId', label: 'Shipper', group: 'Booking' },
  { field: 'pol', label: 'POL', group: 'Routing' },
  { field: 'pod', label: 'POD', group: 'Routing' },
  { field: 'vessel', label: 'Vessel', group: 'Routing' },
  { field: 'voyage', label: 'Voyage', group: 'Routing' },
  { field: 'etd', label: 'ETD', group: 'Routing' },
  { field: 'eta', label: 'ETA', group: 'Routing' },
  { field: 'masterBlNo', label: 'MBL', group: 'Documents' },
  { field: 'houseBlNo', label: 'HBL', group: 'Documents' },
  { field: 'blNumber', label: 'Booking Ref', group: 'Documents' },
  { field: 'freightTerm', label: 'Freight Term', group: 'Documents' },
  { field: 'totalPackages', label: 'Packages', group: 'Cargo' },
  { field: 'totalWeight', label: 'Gross Weight', group: 'Cargo' },
  { field: 'totalVolume', label: 'Volume', group: 'Cargo' },
  { field: 'notes', label: 'Ops Notes', group: 'Cargo', span: 2 as const },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const FMS_AIR_IMPORT_FORM_FIELDS = [
  { field: 'shipmentNo', label: 'Job No', group: 'Identity' },
  { field: 'shipmentType', label: 'Shipment Type', group: 'Identity' },
  { field: 'level', label: 'Direct / House', group: 'Identity' },
  { field: 'status', label: 'Status', group: 'Identity' },
  { field: 'direction', label: 'Direction', group: 'Identity' },
  { field: 'mode', label: 'Mode', group: 'Identity' },
  { field: 'customerId', label: 'Customer', group: 'MAWB / HAWB' },
  { field: 'carrierId', label: 'Airline', group: 'MAWB / HAWB' },
  { field: 'overseasAgentId', label: 'Origin Agent', group: 'MAWB / HAWB' },
  { field: 'masterBlNo', label: 'MAWB', group: 'MAWB / HAWB' },
  { field: 'houseBlNo', label: 'HAWB', group: 'MAWB / HAWB' },
  { field: 'blNumber', label: 'Import Ref', group: 'MAWB / HAWB' },
  { field: 'pol', label: 'Origin Airport', group: 'Flight' },
  { field: 'pod', label: 'Arrival Airport', group: 'Flight' },
  { field: 'vessel', label: 'Flight No', group: 'Flight' },
  { field: 'voyage', label: 'Flight Ref', group: 'Flight' },
  { field: 'etd', label: 'ETD', group: 'Flight' },
  { field: 'eta', label: 'ETA', group: 'Flight' },
  { field: 'freightTerm', label: 'Freight Term', group: 'Cargo' },
  { field: 'totalPackages', label: 'Pieces', group: 'Cargo' },
  { field: 'totalWeight', label: 'Gross Weight', group: 'Cargo' },
  { field: 'totalVolume', label: 'Volume', group: 'Cargo' },
  { field: 'notes', label: 'Ops Notes', group: 'Cargo', span: 2 as const },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const FMS_AIR_EXPORT_FORM_FIELDS = [
  { field: 'shipmentNo', label: 'Job No', group: 'Identity' },
  { field: 'shipmentType', label: 'Shipment Type', group: 'Identity' },
  { field: 'level', label: 'Direct / Consol', group: 'Identity' },
  { field: 'status', label: 'Status', group: 'Identity' },
  { field: 'direction', label: 'Direction', group: 'Identity' },
  { field: 'mode', label: 'Mode', group: 'Identity' },
  { field: 'customerId', label: 'Customer', group: 'Booking' },
  { field: 'shipperId', label: 'Shipper', group: 'Booking' },
  { field: 'carrierId', label: 'Airline / GSA', group: 'Booking' },
  { field: 'masterBlNo', label: 'MAWB', group: 'Booking' },
  { field: 'houseBlNo', label: 'HAWB', group: 'Booking' },
  { field: 'blNumber', label: 'Booking Ref', group: 'Booking' },
  { field: 'pol', label: 'Export Airport', group: 'Flight' },
  { field: 'pod', label: 'Destination Airport', group: 'Flight' },
  { field: 'vessel', label: 'Flight No', group: 'Flight' },
  { field: 'voyage', label: 'Flight Ref', group: 'Flight' },
  { field: 'etd', label: 'ETD', group: 'Flight' },
  { field: 'eta', label: 'ETA', group: 'Flight' },
  { field: 'freightTerm', label: 'Freight Term', group: 'Cargo' },
  { field: 'totalPackages', label: 'Pieces', group: 'Cargo' },
  { field: 'totalWeight', label: 'Gross Weight', group: 'Cargo' },
  { field: 'totalVolume', label: 'Volume', group: 'Cargo' },
  { field: 'notes', label: 'Ops Notes', group: 'Cargo', span: 2 as const },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const SHIPMENT_FORM_FIELDS = [
  { field: 'shipmentNo', label: 'Shipment No', group: 'Identity' },
  { field: 'shipmentType', label: 'Shipment Type', group: 'Identity' },
  { field: 'level', label: 'Level', group: 'Identity' },
  { field: 'status', label: 'Status', group: 'Identity' },
  { field: 'direction', label: 'Direction', group: 'Identity' },
  { field: 'mode', label: 'Mode', group: 'Identity' },
  { field: 'loadType', label: 'Load Type', group: 'Routing' },
  { field: 'customerId', label: 'Customer', group: 'Routing' },
  { field: 'carrierId', label: 'Carrier', group: 'Routing' },
  { field: 'pol', label: 'POL', group: 'Routing' },
  { field: 'pod', label: 'POD', group: 'Routing' },
  { field: 'vessel', label: 'Vessel', group: 'Routing' },
  { field: 'voyage', label: 'Voyage', group: 'Routing' },
  { field: 'etd', label: 'ETD', group: 'Routing' },
  { field: 'eta', label: 'ETA', group: 'Routing' },
  { field: 'masterBlNo', label: 'Master BL', group: 'Documents' },
  { field: 'houseBlNo', label: 'House BL', group: 'Documents' },
  { field: 'blNumber', label: 'BL Number', group: 'Documents' },
  { field: 'freightTerm', label: 'Freight Term', group: 'Documents' },
  { field: 'totalPackages', label: 'Packages', group: 'Totals' },
  { field: 'totalWeight', label: 'Weight', group: 'Totals' },
  { field: 'totalVolume', label: 'Volume', group: 'Totals' },
  { field: 'notes', label: 'Notes', group: 'Totals', span: 2 as const },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const CONTAINER_LIST_COLUMNS = [
  { field: 'job_number', label: 'Job No', width: 120, pinned: 'left' as const },
  { field: 'shipment_no', label: 'Shipment No', width: 130, pinned: 'left' as const },
  { field: 'containerNo', label: 'Container No', width: 160 },
  { field: 'sizeType', label: 'Size/Type', width: 120 },
  { field: 'sealNo', label: 'Seal No', width: 120, editable: true },
  { field: 'status', label: 'Status', width: 110, format: 'status' as const, editable: true },
  { field: 'shipment_level', label: 'Shipment Level', width: 120, format: 'status' as const },
  { field: 'master_bl_no', label: 'MBL', width: 160 },
  { field: 'house_bl_no', label: 'HBL', width: 160 },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const CARGO_LIST_COLUMNS = [
  { field: 'job_number', label: 'Job No', width: 120, pinned: 'left' as const },
  { field: 'shipment_no', label: 'Shipment No', width: 130, pinned: 'left' as const },
  { field: 'containerNo', label: 'Container No', width: 160, editable: true },
  { field: 'lineNo', label: 'Line', width: 70, format: 'number' as const },
  { field: 'description', label: 'Description', width: 240, editable: true },
  { field: 'hsCode', label: 'HS Code', width: 120, editable: true },
  { field: 'marksAndNumbers', label: 'Marks', width: 180, editable: true },
  { field: 'packages', label: 'Packages', width: 110, format: 'number' as const, editable: true },
  { field: 'packageType', label: 'Pkg Type', width: 110, editable: true },
  { field: 'grossWeight', label: 'Weight', width: 120, format: 'number' as const, editable: true },
  { field: 'volume', label: 'Volume', width: 120, format: 'number' as const, editable: true },
  { field: 'isDg', label: 'DG', width: 80, format: 'status' as const, editable: true },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const ROUTE_LIST_COLUMNS = [
  { field: 'job_number', label: 'Job No', width: 120, pinned: 'left' as const },
  { field: 'shipment_no', label: 'Shipment No', width: 130, pinned: 'left' as const },
  { field: 'legNo', label: 'Leg', width: 70, format: 'number' as const },
  { field: 'legType', label: 'Leg Type', width: 120, format: 'status' as const, editable: true },
  { field: 'originPort', label: 'Origin', width: 110, editable: true },
  { field: 'destPort', label: 'Destination', width: 110, editable: true },
  { field: 'vessel', label: 'Vessel', width: 150, editable: true },
  { field: 'voyage', label: 'Voyage', width: 120, editable: true },
  { field: 'etd', label: 'ETD', width: 150, format: 'datetime' as const, editable: true },
  { field: 'eta', label: 'ETA', width: 150, format: 'datetime' as const, editable: true },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const DOCUMENT_LIST_COLUMNS = [
  { field: 'job_number', label: 'Job No', width: 120, pinned: 'left' as const },
  { field: 'shipment_no', label: 'Shipment No', width: 130, pinned: 'left' as const },
  { field: 'fileName', label: 'File Name', width: 220, editable: true },
  { field: 'docType', label: 'Doc Type', width: 140, format: 'status' as const },
  { field: 'docNo', label: 'Doc No', width: 140, editable: true },
  { field: 'status', label: 'Status', width: 110, format: 'status' as const, editable: true },
  { field: 'issueDate', label: 'Issue Date', width: 120, format: 'date' as const, editable: true },
  { field: 'url', label: 'Open', width: 100, format: 'link' as const },
  { field: 'createdAt', label: 'Uploaded', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const EVENT_LIST_COLUMNS = [
  { field: 'job_number', label: 'Job No', width: 120, pinned: 'left' as const },
  { field: 'shipment_no', label: 'Shipment No', width: 130, pinned: 'left' as const },
  { field: 'eventCode', label: 'Event', width: 120, format: 'status' as const },
  { field: 'eventTime', label: 'Event Time', width: 150, format: 'datetime' as const, editable: true },
  { field: 'location', label: 'Location', width: 140, editable: true },
  { field: 'remark', label: 'Remark', width: 220, editable: true },
  { field: 'source', label: 'Source', width: 110, format: 'status' as const, editable: true },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const LEDGER_ENTRY_LIST_COLUMNS = [
  { field: 'job_number', label: 'Job No', width: 120, pinned: 'left' as const },
  { field: 'shipment_nos', label: 'Shipment No', width: 160, pinned: 'left' as const },
  { field: 'entryNumber', label: 'Doc No', width: 150, pinned: 'left' as const },
  { field: 'entryType', label: 'Type', width: 110, format: 'status' as const },
  { field: 'status', label: 'Status', width: 110, format: 'status' as const },
  { field: 'customer_name', label: 'Customer', width: 160 },
  { field: 'vendor_name', label: 'Vendor', width: 160 },
  { field: 'currency', label: 'Ccy', width: 80 },
  { field: 'totalAmount', label: 'Amount', width: 130, format: 'currency' as const },
  { field: 'paidAmount', label: 'Paid', width: 130, format: 'currency' as const },
  { field: 'balance_amount', label: 'Balance', width: 130, format: 'currency' as const },
  { field: 'entryDate', label: 'Entry Date', width: 120, format: 'date' as const },
  { field: 'dueDate', label: 'Due Date', width: 120, format: 'date' as const },
  { field: 'postedAt', label: 'Posted', width: 150, format: 'datetime' as const },
  { field: 'version', label: 'Ver', width: 70, format: 'number' as const },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' as const, pinned: 'right' as const },
  { field: 'updatedAt', label: 'Modified', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const LEDGER_LINE_LIST_COLUMNS = [
  { field: 'job_number', label: 'Job No', width: 120, pinned: 'left' as const },
  { field: 'shipment_nos', label: 'Shipment No', width: 160, pinned: 'left' as const },
  { field: 'entry_number', label: 'Doc No', width: 150, pinned: 'left' as const },
  { field: 'entry_type', label: 'Type', width: 110, format: 'status' as const },
  { field: 'entry_status', label: 'Doc Status', width: 110, format: 'status' as const },
  { field: 'lineNo', label: 'Line', width: 70, format: 'number' as const },
  { field: 'description', label: 'Description', width: 240 },
  { field: 'quantity', label: 'Qty', width: 90, format: 'number' as const },
  { field: 'unitPrice', label: 'Unit Price', width: 120, format: 'currency' as const },
  { field: 'amount', label: 'Amount', width: 120, format: 'currency' as const },
  { field: 'taxRate', label: 'Tax %', width: 90, format: 'number' as const },
  { field: 'taxAmount', label: 'Tax Amt', width: 120, format: 'currency' as const },
  { field: 'debit', label: 'Debit', width: 120, format: 'currency' as const },
  { field: 'credit', label: 'Credit', width: 120, format: 'currency' as const },
  { field: 'version', label: 'Ver', width: 70, format: 'number' as const },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

// Finance column/field constants are now re-exported from finance-view-contracts.ts
// (imported at the top of this file).  The satisfies casts are kept in that file.

// Re-export for any legacy local references (tree-shaking will eliminate unused ones)
export {
  FINANCE_BILL_ENTRY_LIST_COLUMNS,
  FINANCE_JOURNAL_ENTRY_LIST_COLUMNS,
  FINANCE_BILL_LINE_LIST_COLUMNS,
  FINANCE_JOURNAL_LINE_LIST_COLUMNS,
  FINANCE_BILL_ENTRY_FORM_FIELDS,
  FINANCE_JOURNAL_ENTRY_FORM_FIELDS,
  FINANCE_BILL_LINE_FORM_FIELDS,
  FINANCE_JOURNAL_LINE_FORM_FIELDS,
} from '@/lib/finance-view-contracts';

const JOB_COST_LINE_LIST_COLUMNS = [
  { field: 'job_number', label: 'Job No', width: 120, pinned: 'left' as const },
  { field: 'shipment_nos', label: 'Shipment No', width: 160, pinned: 'left' as const },
  { field: 'direction', label: 'Direction', width: 100, format: 'status' as const, editable: true },
  { field: 'billingStatus', label: 'Billing', width: 110, format: 'status' as const, editable: true },
  { field: 'charge_code', label: 'Charge Code', width: 120 },
  { field: 'description', label: 'Description', width: 240, editable: true },
  { field: 'vendor_name', label: 'Vendor', width: 160 },
  { field: 'quantity', label: 'Qty', width: 90, format: 'number' as const, editable: true },
  { field: 'uom', label: 'UOM', width: 90, editable: true },
  { field: 'unitPrice', label: 'Unit Price', width: 120, format: 'currency' as const, editable: true },
  { field: 'amount', label: 'Amount', width: 120, format: 'currency' as const, editable: true },
  { field: 'currency', label: 'Ccy', width: 80, editable: true },
  { field: 'exchangeRate', label: 'X Rate', width: 100, format: 'number' as const, editable: true },
  { field: 'baseAmount', label: 'Local Amt', width: 130, format: 'currency' as const, editable: true },
  { field: 'rate_card_desc', label: 'Rate Card', width: 180 },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' as const, pinned: 'right' as const },
  { field: 'updatedAt', label: 'Modified', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const RATE_CARD_LIST_COLUMNS = [
  { field: 'customer_name', label: 'Customer', width: 170, pinned: 'left' as const },
  { field: 'charge_code', label: 'Charge Code', width: 120, pinned: 'left' as const },
  { field: 'description', label: 'Description', width: 220, editable: true },
  { field: 'status', label: 'Status', width: 100, format: 'status' as const },
  { field: 'direction', label: 'Direction', width: 100, editable: true },
  { field: 'mode', label: 'Mode', width: 90, editable: true },
  { field: 'loadType', label: 'Load Type', width: 100, editable: true },
  { field: 'containerSize', label: 'Container', width: 110, editable: true },
  { field: 'pol', label: 'POL', width: 90, editable: true },
  { field: 'pod', label: 'POD', width: 90, editable: true },
  { field: 'baseRate', label: 'Base Rate', width: 120, format: 'currency' as const, editable: true },
  { field: 'minAmount', label: 'Min Amt', width: 120, format: 'currency' as const, editable: true },
  { field: 'currency', label: 'Ccy', width: 80, editable: true },
  { field: 'uom', label: 'UOM', width: 90, editable: true },
  { field: 'breakBy', label: 'Break By', width: 110, editable: true },
  { field: 'calcMethod', label: 'Calc', width: 90, editable: true },
  { field: 'validFrom', label: 'Valid From', width: 120, format: 'date' as const, editable: true },
  { field: 'validTo', label: 'Valid To', width: 120, format: 'date' as const, editable: true },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const RATE_TIER_LIST_COLUMNS = [
  { field: 'customer_name', label: 'Customer', width: 170, pinned: 'left' as const },
  { field: 'rate_card_desc', label: 'Rate Card', width: 200, pinned: 'left' as const },
  { field: 'charge_code', label: 'Charge Code', width: 120 },
  { field: 'status', label: 'Status', width: 100, format: 'status' as const },
  { field: 'tierSeq', label: 'Tier', width: 80, format: 'number' as const, editable: true },
  { field: 'fromValue', label: 'From', width: 100, format: 'number' as const, editable: true },
  { field: 'toValue', label: 'To', width: 100, format: 'number' as const, editable: true },
  { field: 'rate', label: 'Rate', width: 120, format: 'currency' as const, editable: true },
  { field: 'rateType', label: 'Rate Type', width: 120, editable: true },
  { field: 'minCharge', label: 'Min Charge', width: 130, format: 'currency' as const, editable: true },
  { field: 'currency', label: 'Ccy', width: 80 },
  { field: 'valid_from', label: 'Valid From', width: 120, format: 'date' as const },
  { field: 'valid_to', label: 'Valid To', width: 120, format: 'date' as const },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_IMPORT_SHIPMENT_COLUMNS = [
  { field: 'jobFileNo', label: 'Job No', width: 140, pinned: 'left' as const },
  { field: 'etaDate', label: 'ETA', width: 140, format: 'datetime' as const },
  { field: 'vessel', label: 'Vessel', width: 180 },
  { field: 'voyage', label: 'Voyage', width: 110 },
  { field: 'polCode', label: 'POL', width: 90 },
  { field: 'agentName', label: 'Agent', width: 180 },
  { field: 'createdAt', label: 'Created', width: 170, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_IMPORT_SHIPMENT_DETAIL_SECTIONS = [
  {
    title: 'Identity',
    columns: 3 as const,
    fields: [
      { field: 'jobFileNo', label: 'Job No' },
      { field: 'masterBlNo', label: 'MBL' },
      { field: 'houseBlNo', label: 'HBL' },
      { field: 'agentName', label: 'Agent' },
      { field: 'createdAt', label: 'Created', format: 'datetime' },
      { field: 'etaDate', label: 'ETA', format: 'datetime' },
    ],
  },
  {
    title: 'Routing',
    columns: 3 as const,
    fields: [
      { field: 'vessel', label: 'Vessel' },
      { field: 'voyage', label: 'Voyage' },
      { field: 'polCode', label: 'POL' },
      { field: 'podCode', label: 'POD' },
      { field: 'serviceModeFrom', label: 'From' },
      { field: 'serviceModeTo', label: 'To' },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const PRO2_EXPORT_SHIPMENT_COLUMNS = [
  { field: 'jobFileNo', label: 'Job No', width: 140, pinned: 'left' as const },
  { field: 'etdDate', label: 'ETD', width: 140, format: 'datetime' as const },
  { field: 'vessel', label: 'Vessel', width: 180 },
  { field: 'voyage', label: 'Voyage', width: 110 },
  { field: 'polCode', label: 'POL', width: 90 },
  { field: 'agentName', label: 'Agent', width: 180 },
  { field: 'createdAt', label: 'Created', width: 170, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_EXPORT_SHIPMENT_DETAIL_SECTIONS = [
  {
    title: 'Identity',
    columns: 3 as const,
    fields: [
      { field: 'jobFileNo', label: 'Job No' },
      { field: 'bookingNo', label: 'Booking No' },
      { field: 'masterBlNo', label: 'MBL' },
      { field: 'agentName', label: 'Agent' },
      { field: 'createdAt', label: 'Created', format: 'datetime' },
      { field: 'etdDate', label: 'ETD', format: 'datetime' },
    ],
  },
  {
    title: 'Routing',
    columns: 3 as const,
    fields: [
      { field: 'vessel', label: 'Vessel' },
      { field: 'voyage', label: 'Voyage' },
      { field: 'polCode', label: 'POL' },
      { field: 'podCode', label: 'POD' },
      { field: 'serviceModeFrom', label: 'From' },
      { field: 'serviceModeTo', label: 'To' },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const PRO2_IMPORT_CONTAINER_COLUMNS = [
  { field: 'jobFileNo', label: 'Job No', width: 140, pinned: 'left' as const },
  { field: 'containerNo', label: 'Container', width: 140 },
  { field: 'sealNo', label: 'Seal', width: 120 },
  { field: 'sizeName', label: 'Size', width: 90 },
  { field: 'typeCode', label: 'Type', width: 90 },
  { field: 'masterBlNo', label: 'MBL', width: 160 },
  { field: 'etaDate', label: 'ETA', width: 140, format: 'datetime' as const },
  { field: 'vessel', label: 'Vessel', width: 180 },
  { field: 'voyage', label: 'Voyage', width: 110 },
  { field: 'polCode', label: 'POL', width: 90 },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_EXPORT_CONTAINER_COLUMNS = [
  { field: 'jobFileNo', label: 'Job No', width: 140, pinned: 'left' as const },
  { field: 'containerNo', label: 'Container', width: 140 },
  { field: 'sealNo', label: 'Seal', width: 120 },
  { field: 'sizeName', label: 'Size', width: 90 },
  { field: 'typeCode', label: 'Type', width: 90 },
  { field: 'masterBlNo', label: 'MBL', width: 160 },
  { field: 'etdDate', label: 'ETD', width: 140, format: 'datetime' as const },
  { field: 'vessel', label: 'Vessel', width: 180 },
  { field: 'voyage', label: 'Voyage', width: 110 },
  { field: 'polCode', label: 'POL', width: 90 },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_IMPORT_CARGO_COLUMNS = [
  { field: 'jobFileNo', label: 'Job No', width: 140, pinned: 'left' as const },
  { field: 'blNo', label: 'BL No', width: 140 },
  { field: 'containerNo', label: 'Container', width: 140 },
  { field: 'packages', label: 'Pkgs', width: 80, format: 'number' as const },
  { field: 'unit', label: 'Unit', width: 90 },
  { field: 'kgs', label: 'KGS', width: 90, format: 'number' as const },
  { field: 'cbm', label: 'CBM', width: 90, format: 'number' as const },
  { field: 'description', label: 'Cargo', width: 280 },
  { field: 'etaDate', label: 'ETA', width: 140, format: 'datetime' as const },
  { field: 'podCode', label: 'POD', width: 100 },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_EXPORT_CARGO_COLUMNS = [
  { field: 'jobFileNo', label: 'Job No', width: 140, pinned: 'left' as const },
  { field: 'bookingNo', label: 'Booking', width: 140 },
  { field: 'blNo', label: 'BL No', width: 140 },
  { field: 'containerNo', label: 'Container', width: 140 },
  { field: 'packages', label: 'Pkgs', width: 120 },
  { field: 'description', label: 'Cargo', width: 320 },
  { field: 'etdDate', label: 'ETD', width: 140, format: 'datetime' as const },
  { field: 'polCode', label: 'POL', width: 90 },
  { field: 'podCode', label: 'POD', width: 100 },
  { field: 'createdAt', label: 'Created', width: 170, format: 'datetime' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_EXPORT_BOOKING_COLUMNS = [
  { field: 'jobFileNo', label: 'Job No', width: 140, pinned: 'left' as const },
  { field: 'bookingNo', label: 'Booking No', width: 150 },
  { field: 'dgIndicator', label: 'DG', width: 70, format: 'status' as const },
  { field: 'destPortCode', label: 'Dest Port', width: 100 },
  { field: 'shipperCode', label: 'Shipper', width: 120 },
  { field: 'cargoType', label: 'Cargo Type', width: 90 },
  { field: 'commodity', label: 'Commodity', width: 130 },
  { field: 'packages', label: 'Pkgs', width: 120 },
  { field: 'serviceModeFrom', label: 'From', width: 90 },
  { field: 'serviceModeTo', label: 'To', width: 90 },
  { field: 'vessel', label: 'Vessel', width: 180 },
  { field: 'voyage', label: 'Voyage', width: 100 },
  { field: 'etdDate', label: 'ETD', width: 140, format: 'datetime' as const },
  { field: 'polCode', label: 'POL', width: 90 },
  { field: 'description', label: 'Cargo', width: 320 },
  { field: 'createdAt', label: 'Created', width: 170, format: 'datetime' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_EXPORT_BOOKING_DETAIL_SECTIONS = [
  {
    title: 'Identity',
    columns: 3 as const,
    fields: [
      { field: 'jobFileNo', label: 'Job No' },
      { field: 'bookingNo', label: 'Booking No' },
      { field: 'dgIndicator', label: 'DG' },
      { field: 'shipperCode', label: 'Shipper' },
      { field: 'cargoType', label: 'Cargo Type' },
      { field: 'commodity', label: 'Commodity' },
    ],
  },
  {
    title: 'Routing',
    columns: 3 as const,
    fields: [
      { field: 'vessel', label: 'Vessel' },
      { field: 'voyage', label: 'Voyage' },
      { field: 'polCode', label: 'POL' },
      { field: 'destPortCode', label: 'Dest Port' },
      { field: 'serviceModeFrom', label: 'From' },
      { field: 'serviceModeTo', label: 'To' },
    ],
  },
  {
    title: 'Cargo',
    columns: 2 as const,
    fields: [
      { field: 'packages', label: 'Packages' },
      { field: 'etdDate', label: 'ETD', format: 'datetime' },
      { field: 'description', label: 'Cargo Description', span: 2 },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const PRO2_FIN_DOCUMENT_COLUMNS = [
  { field: 'invCnNo', label: 'Doc No', width: 150, pinned: 'left' as const },
  { field: 'jobFileId', label: 'Job File Id', width: 110 },
  { field: 'documentType', label: 'Doc Type', width: 90, format: 'status' as const },
  { field: 'companyCode', label: 'Company', width: 120 },
  { field: 'companyName', label: 'Company Name', width: 220 },
  { field: 'issueDate', label: 'Issue Date', width: 140, format: 'datetime' as const },
  { field: 'currencyCode', label: 'Curr', width: 80 },
  { field: 'totalAmount', label: 'Total', width: 110, format: 'currency' as const },
  { field: 'outstanding', label: 'Outstanding', width: 110, format: 'currency' as const },
  { field: 'pdfCount', label: 'PDF', width: 70, format: 'number' as const },
  { field: 'emailCount', label: 'Email', width: 70, format: 'number' as const },
  { field: 'createdAt', label: 'Created', width: 170, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_FIN_DOCUMENT_DETAIL_SECTIONS = [
  {
    title: 'Header',
    columns: 6 as const,
    fields: [
      { field: 'invCnNo', label: 'Doc No' },
      { field: 'documentType', label: 'Doc Type' },
      { field: 'status', label: 'Status' },
      { field: 'companyCode', label: 'Company Code' },
      { field: 'companyName', label: 'Company Name', span: 2 },
      { field: 'jobFileId', label: 'Job File Id' },
      { field: 'issueDate', label: 'Issue Date', format: 'datetime' },
      { field: 'jobDate', label: 'Job Date', format: 'datetime' },
      { field: 'paymentDate', label: 'Payment Date', format: 'datetime' },
      { field: 'currencyCode', label: 'Currency' },
      { field: 'pdfCount', label: 'PDF Count' },
      { field: 'emailCount', label: 'Email Count' },
    ],
  },
  {
    title: 'Amounts',
    columns: 6 as const,
    fields: [
      { field: 'totalAmount', label: 'Total', format: 'currency' },
      { field: 'localAmount', label: 'Local Amount', format: 'currency' },
      { field: 'outstanding', label: 'Outstanding', format: 'currency' },
      { field: 'createdAt', label: 'Created', format: 'datetime' },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const PRO2_FIN_CHARGE_COLUMNS = [
  { field: 'invCnNo', label: 'Doc No', width: 150, pinned: 'left' as const },
  { field: 'jobFileId', label: 'Job File Id', width: 110 },
  { field: 'documentType', label: 'Doc Type', width: 90, format: 'status' as const },
  { field: 'lineNo', label: 'Line', width: 70, format: 'number' as const },
  { field: 'chargeCode', label: 'Charge Code', width: 110 },
  { field: 'chargeName', label: 'Charge Name', width: 220 },
  { field: 'currencyCode', label: 'Curr', width: 80 },
  { field: 'rate', label: 'Rate', width: 90, format: 'number' as const },
  { field: 'quantity', label: 'Qty', width: 90, format: 'number' as const },
  { field: 'amount', label: 'Amount', width: 100, format: 'currency' as const },
  { field: 'localAmount', label: 'Local Amt', width: 110, format: 'currency' as const },
  { field: 'isTax', label: 'Tax', width: 70, format: 'status' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_FIN_FX_COLUMNS = [
  { field: 'invCnNo', label: 'Doc No', width: 150, pinned: 'left' as const },
  { field: 'jobFileId', label: 'Job File Id', width: 110 },
  { field: 'leftCode', label: 'Left Curr', width: 90 },
  { field: 'leftRate', label: 'Left Rate', width: 90, format: 'number' as const },
  { field: 'rightCode', label: 'Right Curr', width: 90 },
  { field: 'rightRate', label: 'Right Rate', width: 90, format: 'number' as const },
  { field: 'currencyCode', label: 'Doc Curr', width: 90 },
  { field: 'createdAt', label: 'Created', width: 170, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_MASTER_COMPANY_COLUMNS = [
  { field: 'companyCode', label: 'Code', width: 120, pinned: 'left' as const },
  { field: 'companyName', label: 'Company Name', width: 260, pinned: 'left' as const },
  { field: 'shortName', label: 'Short Name', width: 180 },
  { field: 'portCode', label: 'Port', width: 90 },
  { field: 'airportCode', label: 'Airport', width: 90 },
  { field: 'isActive', label: 'Active', width: 90, format: 'status' as const },
  { field: 'monthlyStatement', label: 'Statement', width: 90, format: 'status' as const },
  { field: 'isGlobal', label: 'Global', width: 90, format: 'status' as const },
  { field: 'emailNoticeFlag', label: 'Email Notice', width: 110, format: 'status' as const },
  { field: 'lastTransactionDate', label: 'Last Txn', width: 160, format: 'datetime' as const },
  { field: 'createdAt', label: 'Created', width: 170, format: 'datetime' as const, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const PRO2_MASTER_COMPANY_DETAIL_SECTIONS = [
  {
    title: 'Identity',
    columns: 6 as const,
    fields: [
      { field: 'companyCode', label: 'Company Code' },
      { field: 'shortName', label: 'Short Name' },
      { field: 'isActive', label: 'Active', format: 'status' },
      { field: 'monthlyStatement', label: 'Monthly Statement', format: 'status' },
      { field: 'isGlobal', label: 'Global', format: 'status' },
      { field: 'emailNoticeFlag', label: 'Email Notice', format: 'status' },
      { field: 'companyName', label: 'Company Name', span: 3 },
      { field: 'portCode', label: 'Port Code' },
      { field: 'airportCode', label: 'Airport Code' },
      { field: 'lastTransactionDate', label: 'Last Transaction', format: 'datetime' },
    ],
  },
  {
    title: 'Address',
    columns: 6 as const,
    fields: [
      { field: 'address', label: 'Address', span: 6 },
      { field: 'createdAt', label: 'Created', format: 'datetime' },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const OI_COMMON_LIST_BANNERS = [
  { tone: 'info' as const, text: 'Double-click editable cells to update live records. Use New to add a row.' },
  { tone: 'info' as const, text: 'Bulk CSV import is available from Operational Intelligence > Registers.' },
];

const OI_SITE_TYPE_OPTIONS = ['retail', 'truck-centre', 'warehouse'];
const OI_SERVICE_TYPE_OPTIONS = ['Alignment', 'Balancing', 'Battery & Tyre Bundle', 'Fleet Inspection', 'Puncture Repair', 'Tyre Replacement'];
const OI_VEHICLE_SEGMENT_OPTIONS = ['Fleet', 'Heavy Truck', 'Light Truck', 'Passenger', 'SUV', 'Van'];
const OI_INCIDENT_CATEGORY_OPTIONS = [
  'Customer Complaint',
  'Delivery / Installation Miss',
  'Service Delay',
  'Stock Discrepancy',
  'Vehicle Damage',
  'Warranty Investigation',
];
const OI_INCIDENT_SEVERITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const OI_INCIDENT_STATUS_OPTIONS = ['Open', 'Investigating', 'Closed'];
const OI_INCIDENT_OWNER_OPTIONS = ['Ops Manager', 'Warehouse Lead'];
const OI_REPEAT_CLUSTER_OPTIONS = ['afternoon-bay-overload', 'alignment-rework-cluster', 'mixed-ops-signals'];
const OI_WARRANTY_CATEGORY_OPTIONS = ['Irregular Wear', 'Noise / Vibration', 'Premature Tread Wear', 'Sidewall Damage Review'];
const OI_WARRANTY_STATUS_OPTIONS = ['Pending Inspection', 'Under Review', 'Closed'];
const OI_INVENTORY_SIGNAL_TYPE_OPTIONS = ['Abnormal Consumption', 'Low Cover', 'Negative Variance', 'Repeat Stock Count Variance', 'Slow Moving Cluster'];
const OI_SEVERITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const OI_EHS_EVENT_TYPE_OPTIONS = ['Equipment Handling', 'Forklift / Warehouse Risk', 'Near Miss', 'Slip / Trip Hazard', 'Unsafe Act', 'Workshop Housekeeping'];
const OI_EHS_STATUS_OPTIONS = ['Closed', 'Action Open', 'Escalated'];
const OI_EHS_OWNER_OPTIONS = ['Warehouse Supervisor', 'Workshop Supervisor'];
const OI_FLEET_SEGMENT_OPTIONS = [
  'Commercial Lease',
  'Commercial Vans',
  'Construction Fleet',
  'Delivery Fleet',
  'Heavy Vehicle',
  'Industrial Fleet',
  'Maintenance Fleet',
  'Passenger Fleet',
  'Refrigerated Fleet',
  'Regional Fleet',
  'Retail Distribution',
  'Shuttle Fleet',
];
const OI_FLEET_RISK_OPTIONS = ['Low', 'Medium', 'High'];

function oiOptions(values: string[]) {
  return values.map((value) => ({ label: value, value }));
}

const OI_SERVICE_JOB_LIST_COLUMNS = [
  { field: 'jobNo', label: 'Job No', width: 150, pinned: 'left' as const },
  { field: 'openedAt', label: 'Opened', width: 160, format: 'datetime' as const, sortable: true },
  { field: 'site', label: 'Site', width: 180, editable: true },
  { field: 'siteType', label: 'Site Type', width: 120, format: 'status' as const, editable: true },
  { field: 'serviceType', label: 'Service Type', width: 180, editable: true },
  { field: 'vehicleSegment', label: 'Vehicle', width: 120, format: 'status' as const, editable: true },
  { field: 'technician', label: 'Technician', width: 140, editable: true },
  { field: 'fleetAccount', label: 'Fleet Account', width: 180, editable: true },
  { field: 'delayMinutes', label: 'Delay Min', width: 110, format: 'number' as const, editable: true },
  { field: 'invoiceValue', label: 'Invoice', width: 110, format: 'currency' as const, editable: true },
  { field: 'reworkFlag', label: 'Rework', width: 90, format: 'status' as const, editable: true },
  { field: 'complaintLinked', label: 'Complaint', width: 100, format: 'status' as const, editable: true },
  { field: 'status', label: 'Status', width: 120, format: 'status' as const, editable: true, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const OI_SERVICE_JOB_FILTERS = [
  { field: 'site', label: 'Site', type: 'text' as const },
  { field: 'siteType', label: 'Site Type', type: 'select' as const, options: oiOptions(OI_SITE_TYPE_OPTIONS) },
  { field: 'serviceType', label: 'Service Type', type: 'select' as const, options: oiOptions(OI_SERVICE_TYPE_OPTIONS) },
  { field: 'vehicleSegment', label: 'Vehicle Segment', type: 'select' as const, options: oiOptions(OI_VEHICLE_SEGMENT_OPTIONS) },
] satisfies NonNullable<ViewTemplateDSL['filters']>;

const OI_SERVICE_JOB_FORM_FIELDS = [
  { field: 'jobNo', label: 'Job No', required: true, span: 1, group: 'Identity' },
  { field: 'status', label: 'Status', required: true, type: 'select' as const, options: oiOptions(['Open', 'In Progress', 'Closed', 'Cancelled']), span: 1, group: 'Identity' },
  { field: 'openedAt', label: 'Opened At', required: true, type: 'date' as const, span: 1, group: 'Timeline' },
  { field: 'closedAt', label: 'Closed At', type: 'date' as const, span: 1, group: 'Timeline' },
  { field: 'site', label: 'Site', required: true, span: 2, group: 'Operation' },
  { field: 'siteCode', label: 'Site Code', span: 1, group: 'Operation' },
  { field: 'siteType', label: 'Site Type', type: 'select' as const, options: oiOptions(OI_SITE_TYPE_OPTIONS), span: 1, group: 'Operation' },
  { field: 'serviceType', label: 'Service Type', type: 'select' as const, options: oiOptions(OI_SERVICE_TYPE_OPTIONS), span: 1, group: 'Operation' },
  { field: 'vehicleSegment', label: 'Vehicle Segment', type: 'select' as const, options: oiOptions(OI_VEHICLE_SEGMENT_OPTIONS), span: 1, group: 'Operation' },
  { field: 'technician', label: 'Technician', span: 1, group: 'Operation' },
  { field: 'fleetAccount', label: 'Fleet Account', span: 2, group: 'Operation' },
  { field: 'skuCode', label: 'SKU Code', span: 1, group: 'Operation' },
  { field: 'promisedMinutes', label: 'Promised Minutes', type: 'number' as const, span: 1, group: 'Performance' },
  { field: 'turnaroundMinutes', label: 'Turnaround Minutes', type: 'number' as const, span: 1, group: 'Performance' },
  { field: 'delayMinutes', label: 'Delay Minutes', type: 'number' as const, span: 1, group: 'Performance' },
  { field: 'invoiceValue', label: 'Invoice Value', type: 'number' as const, span: 1, group: 'Performance' },
  { field: 'reworkFlag', label: 'Rework Flag', type: 'toggle' as const, span: 1, group: 'Signals' },
  { field: 'complaintLinked', label: 'Complaint Linked', type: 'toggle' as const, span: 1, group: 'Signals' },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const OI_SERVICE_JOB_DETAIL_SECTIONS = [
  {
    title: 'Identity',
    columns: 6 as const,
    fields: [
      { field: 'jobNo', label: 'Job No' },
      { field: 'status', label: 'Status', format: 'status' },
      { field: 'site', label: 'Site', span: 2 },
      { field: 'siteCode', label: 'Site Code' },
      { field: 'siteType', label: 'Site Type', format: 'status' },
      { field: 'serviceType', label: 'Service Type', span: 2 },
      { field: 'vehicleSegment', label: 'Vehicle Segment', format: 'status' },
      { field: 'technician', label: 'Technician' },
      { field: 'fleetAccount', label: 'Fleet Account', span: 2 },
      { field: 'skuCode', label: 'SKU Code' },
    ],
  },
  {
    title: 'Timeline & Performance',
    columns: 6 as const,
    fields: [
      { field: 'openedAt', label: 'Opened At', format: 'datetime' },
      { field: 'closedAt', label: 'Closed At', format: 'datetime' },
      { field: 'promisedMinutes', label: 'Promised Minutes', format: 'number' },
      { field: 'turnaroundMinutes', label: 'Turnaround Minutes', format: 'number' },
      { field: 'delayMinutes', label: 'Delay Minutes', format: 'number' },
      { field: 'invoiceValue', label: 'Invoice Value', format: 'currency' },
      { field: 'reworkFlag', label: 'Rework Flag', format: 'status' },
      { field: 'complaintLinked', label: 'Complaint Linked', format: 'status' },
    ],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const OI_INCIDENT_LIST_COLUMNS = [
  { field: 'incidentId', label: 'Incident ID', width: 150, pinned: 'left' as const },
  { field: 'occurredAt', label: 'Occurred', width: 160, format: 'datetime' as const },
  { field: 'site', label: 'Site', width: 180, editable: true },
  { field: 'category', label: 'Category', width: 180, editable: true },
  { field: 'severity', label: 'Severity', width: 100, format: 'status' as const, editable: true },
  { field: 'status', label: 'Status', width: 120, format: 'status' as const, editable: true },
  { field: 'owner', label: 'Owner', width: 140, editable: true },
  { field: 'repeatCluster', label: 'Repeat Cluster', width: 170, editable: true },
  { field: 'linkedJobNo', label: 'Linked Job', width: 150, editable: true },
  { field: 'rootCause', label: 'Root Cause', width: 220, editable: true, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const OI_INCIDENT_FILTERS = [
  { field: 'site', label: 'Site', type: 'text' as const },
  { field: 'category', label: 'Category', type: 'select' as const, options: oiOptions(OI_INCIDENT_CATEGORY_OPTIONS) },
  { field: 'severity', label: 'Severity', type: 'select' as const, options: oiOptions(OI_INCIDENT_SEVERITY_OPTIONS) },
  { field: 'owner', label: 'Owner', type: 'select' as const, options: oiOptions(OI_INCIDENT_OWNER_OPTIONS) },
] satisfies NonNullable<ViewTemplateDSL['filters']>;

const OI_INCIDENT_FORM_FIELDS = [
  { field: 'incidentId', label: 'Incident ID', required: true, group: 'Identity' },
  { field: 'occurredAt', label: 'Occurred At', required: true, type: 'date' as const, group: 'Identity' },
  { field: 'status', label: 'Status', required: true, type: 'select' as const, options: oiOptions(OI_INCIDENT_STATUS_OPTIONS), group: 'Identity' },
  { field: 'severity', label: 'Severity', required: true, type: 'select' as const, options: oiOptions(OI_INCIDENT_SEVERITY_OPTIONS), group: 'Identity' },
  { field: 'site', label: 'Site', required: true, span: 2, group: 'Case' },
  { field: 'category', label: 'Category', required: true, type: 'select' as const, options: oiOptions(OI_INCIDENT_CATEGORY_OPTIONS), span: 2, group: 'Case' },
  { field: 'owner', label: 'Owner', type: 'select' as const, options: oiOptions(OI_INCIDENT_OWNER_OPTIONS), group: 'Case' },
  { field: 'repeatCluster', label: 'Repeat Cluster', type: 'select' as const, options: oiOptions(OI_REPEAT_CLUSTER_OPTIONS), group: 'Case' },
  { field: 'linkedJobNo', label: 'Linked Job No', group: 'Case' },
  { field: 'rootCause', label: 'Root Cause', type: 'textarea' as const, span: 2, group: 'Resolution' },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const OI_INCIDENT_DETAIL_SECTIONS = [
  {
    title: 'Incident Summary',
    columns: 6 as const,
    fields: [
      { field: 'incidentId', label: 'Incident ID' },
      { field: 'status', label: 'Status', format: 'status' },
      { field: 'severity', label: 'Severity', format: 'status' },
      { field: 'occurredAt', label: 'Occurred At', format: 'datetime' },
      { field: 'site', label: 'Site', span: 2 },
      { field: 'category', label: 'Category', span: 2 },
      { field: 'owner', label: 'Owner' },
      { field: 'repeatCluster', label: 'Repeat Cluster' },
      { field: 'linkedJobNo', label: 'Linked Job No' },
    ],
  },
  {
    title: 'Analysis',
    columns: 6 as const,
    fields: [{ field: 'rootCause', label: 'Root Cause', span: 6 }],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const OI_WARRANTY_LIST_COLUMNS = [
  { field: 'caseId', label: 'Case ID', width: 150, pinned: 'left' as const },
  { field: 'openedAt', label: 'Opened', width: 160, format: 'datetime' as const },
  { field: 'site', label: 'Site', width: 180, editable: true },
  { field: 'category', label: 'Category', width: 180, editable: true },
  { field: 'vehicleSegment', label: 'Vehicle', width: 120, format: 'status' as const, editable: true },
  { field: 'skuCode', label: 'SKU', width: 150, editable: true },
  { field: 'linkedJobNo', label: 'Linked Job', width: 150, editable: true },
  { field: 'responseHours', label: 'Response Hrs', width: 120, format: 'number' as const, editable: true },
  { field: 'status', label: 'Status', width: 140, format: 'status' as const, editable: true },
  { field: 'outcome', label: 'Outcome', width: 220, editable: true, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const OI_WARRANTY_FILTERS = [
  { field: 'site', label: 'Site', type: 'text' as const },
  { field: 'category', label: 'Category', type: 'select' as const, options: oiOptions(OI_WARRANTY_CATEGORY_OPTIONS) },
  { field: 'vehicleSegment', label: 'Vehicle Segment', type: 'select' as const, options: oiOptions(OI_VEHICLE_SEGMENT_OPTIONS) },
  { field: 'status', label: 'Status', type: 'select' as const, options: oiOptions(OI_WARRANTY_STATUS_OPTIONS) },
] satisfies NonNullable<ViewTemplateDSL['filters']>;

const OI_WARRANTY_FORM_FIELDS = [
  { field: 'caseId', label: 'Case ID', required: true, group: 'Identity' },
  { field: 'openedAt', label: 'Opened At', required: true, type: 'date' as const, group: 'Identity' },
  { field: 'status', label: 'Status', required: true, type: 'select' as const, options: oiOptions(OI_WARRANTY_STATUS_OPTIONS), group: 'Identity' },
  { field: 'site', label: 'Site', required: true, span: 2, group: 'Case' },
  { field: 'category', label: 'Category', required: true, type: 'select' as const, options: oiOptions(OI_WARRANTY_CATEGORY_OPTIONS), group: 'Case' },
  { field: 'vehicleSegment', label: 'Vehicle Segment', type: 'select' as const, options: oiOptions(OI_VEHICLE_SEGMENT_OPTIONS), group: 'Case' },
  { field: 'skuCode', label: 'SKU Code', group: 'Case' },
  { field: 'linkedJobNo', label: 'Linked Job No', group: 'Case' },
  { field: 'responseHours', label: 'Response Hours', type: 'number' as const, group: 'Resolution' },
  { field: 'outcome', label: 'Outcome', type: 'textarea' as const, span: 2, group: 'Resolution' },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const OI_WARRANTY_DETAIL_SECTIONS = [
  {
    title: 'Case Summary',
    columns: 6 as const,
    fields: [
      { field: 'caseId', label: 'Case ID' },
      { field: 'status', label: 'Status', format: 'status' },
      { field: 'openedAt', label: 'Opened At', format: 'datetime' },
      { field: 'site', label: 'Site', span: 2 },
      { field: 'category', label: 'Category', span: 2 },
      { field: 'vehicleSegment', label: 'Vehicle Segment', format: 'status' },
      { field: 'skuCode', label: 'SKU Code' },
      { field: 'linkedJobNo', label: 'Linked Job No' },
      { field: 'responseHours', label: 'Response Hours', format: 'number' },
    ],
  },
  {
    title: 'Outcome',
    columns: 6 as const,
    fields: [{ field: 'outcome', label: 'Outcome', span: 6 }],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const OI_INVENTORY_LIST_COLUMNS = [
  { field: 'signalId', label: 'Signal ID', width: 150, pinned: 'left' as const },
  { field: 'detectedAt', label: 'Detected', width: 160, format: 'datetime' as const },
  { field: 'site', label: 'Site', width: 180, editable: true },
  { field: 'signalType', label: 'Signal Type', width: 190, editable: true },
  { field: 'severity', label: 'Severity', width: 100, format: 'status' as const, editable: true },
  { field: 'skuCode', label: 'SKU', width: 150, editable: true },
  { field: 'daysOfCover', label: 'Days Cover', width: 110, format: 'number' as const, editable: true },
  { field: 'varianceQty', label: 'Variance Qty', width: 120, format: 'number' as const, editable: true },
  { field: 'suggestedAction', label: 'Suggested Action', width: 240, editable: true, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const OI_INVENTORY_FILTERS = [
  { field: 'site', label: 'Site', type: 'text' as const },
  { field: 'signalType', label: 'Signal Type', type: 'select' as const, options: oiOptions(OI_INVENTORY_SIGNAL_TYPE_OPTIONS) },
  { field: 'severity', label: 'Severity', type: 'select' as const, options: oiOptions(OI_SEVERITY_OPTIONS) },
  { field: 'skuCode', label: 'SKU', type: 'text' as const },
] satisfies NonNullable<ViewTemplateDSL['filters']>;

const OI_INVENTORY_FORM_FIELDS = [
  { field: 'signalId', label: 'Signal ID', required: true, group: 'Identity' },
  { field: 'detectedAt', label: 'Detected At', required: true, type: 'date' as const, group: 'Identity' },
  { field: 'severity', label: 'Severity', required: true, type: 'select' as const, options: oiOptions(OI_SEVERITY_OPTIONS), group: 'Identity' },
  { field: 'site', label: 'Site', required: true, span: 2, group: 'Signal' },
  { field: 'signalType', label: 'Signal Type', required: true, type: 'select' as const, options: oiOptions(OI_INVENTORY_SIGNAL_TYPE_OPTIONS), span: 2, group: 'Signal' },
  { field: 'skuCode', label: 'SKU Code', group: 'Signal' },
  { field: 'daysOfCover', label: 'Days of Cover', type: 'number' as const, group: 'Signal' },
  { field: 'varianceQty', label: 'Variance Qty', type: 'number' as const, group: 'Signal' },
  { field: 'suggestedAction', label: 'Suggested Action', type: 'textarea' as const, span: 2, group: 'Action' },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const OI_INVENTORY_DETAIL_SECTIONS = [
  {
    title: 'Signal Summary',
    columns: 6 as const,
    fields: [
      { field: 'signalId', label: 'Signal ID' },
      { field: 'severity', label: 'Severity', format: 'status' },
      { field: 'detectedAt', label: 'Detected At', format: 'datetime' },
      { field: 'site', label: 'Site', span: 2 },
      { field: 'signalType', label: 'Signal Type', span: 2 },
      { field: 'skuCode', label: 'SKU Code' },
      { field: 'daysOfCover', label: 'Days of Cover', format: 'number' },
      { field: 'varianceQty', label: 'Variance Qty', format: 'number' },
    ],
  },
  {
    title: 'Recommended Action',
    columns: 6 as const,
    fields: [{ field: 'suggestedAction', label: 'Suggested Action', span: 6 }],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const OI_EHS_LIST_COLUMNS = [
  { field: 'eventId', label: 'Event ID', width: 150, pinned: 'left' as const },
  { field: 'occurredAt', label: 'Occurred', width: 160, format: 'datetime' as const },
  { field: 'site', label: 'Site', width: 180, editable: true },
  { field: 'eventType', label: 'Event Type', width: 180, editable: true },
  { field: 'severity', label: 'Severity', width: 100, format: 'status' as const, editable: true },
  { field: 'status', label: 'Status', width: 120, format: 'status' as const, editable: true },
  { field: 'lostTime', label: 'Lost Time', width: 100, format: 'status' as const, editable: true },
  { field: 'owner', label: 'Owner', width: 160, editable: true },
  { field: 'rootCause', label: 'Root Cause', width: 240, editable: true, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const OI_EHS_FILTERS = [
  { field: 'site', label: 'Site', type: 'text' as const },
  { field: 'eventType', label: 'Event Type', type: 'select' as const, options: oiOptions(OI_EHS_EVENT_TYPE_OPTIONS) },
  { field: 'severity', label: 'Severity', type: 'select' as const, options: oiOptions(['Low', 'Medium', 'High']) },
  { field: 'status', label: 'Status', type: 'select' as const, options: oiOptions(OI_EHS_STATUS_OPTIONS) },
] satisfies NonNullable<ViewTemplateDSL['filters']>;

const OI_EHS_FORM_FIELDS = [
  { field: 'eventId', label: 'Event ID', required: true, group: 'Identity' },
  { field: 'occurredAt', label: 'Occurred At', required: true, type: 'date' as const, group: 'Identity' },
  { field: 'status', label: 'Status', required: true, type: 'select' as const, options: oiOptions(OI_EHS_STATUS_OPTIONS), group: 'Identity' },
  { field: 'severity', label: 'Severity', required: true, type: 'select' as const, options: oiOptions(['Low', 'Medium', 'High']), group: 'Identity' },
  { field: 'site', label: 'Site', required: true, span: 2, group: 'Event' },
  { field: 'eventType', label: 'Event Type', required: true, type: 'select' as const, options: oiOptions(OI_EHS_EVENT_TYPE_OPTIONS), span: 2, group: 'Event' },
  { field: 'lostTime', label: 'Lost Time', type: 'toggle' as const, group: 'Event' },
  { field: 'owner', label: 'Owner', type: 'select' as const, options: oiOptions(OI_EHS_OWNER_OPTIONS), group: 'Action' },
  { field: 'rootCause', label: 'Root Cause', type: 'textarea' as const, span: 2, group: 'Action' },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const OI_EHS_DETAIL_SECTIONS = [
  {
    title: 'Event Summary',
    columns: 6 as const,
    fields: [
      { field: 'eventId', label: 'Event ID' },
      { field: 'status', label: 'Status', format: 'status' },
      { field: 'severity', label: 'Severity', format: 'status' },
      { field: 'occurredAt', label: 'Occurred At', format: 'datetime' },
      { field: 'site', label: 'Site', span: 2 },
      { field: 'eventType', label: 'Event Type', span: 2 },
      { field: 'lostTime', label: 'Lost Time', format: 'status' },
      { field: 'owner', label: 'Owner' },
    ],
  },
  {
    title: 'Cause & Action',
    columns: 6 as const,
    fields: [{ field: 'rootCause', label: 'Root Cause', span: 6 }],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

const OI_FLEET_LIST_COLUMNS = [
  { field: 'month', label: 'Month', width: 120, format: 'date' as const, pinned: 'left' as const },
  { field: 'accountName', label: 'Account', width: 200, editable: true, pinned: 'left' as const },
  { field: 'segment', label: 'Segment', width: 160, editable: true },
  { field: 'anchorSite', label: 'Anchor Site', width: 180, editable: true },
  { field: 'revenue', label: 'Revenue', width: 120, format: 'currency' as const, editable: true },
  { field: 'serviceJobs', label: 'Jobs', width: 90, format: 'number' as const, editable: true },
  { field: 'complaintCount', label: 'Complaints', width: 110, format: 'number' as const, editable: true },
  { field: 'delayCount', label: 'Delays', width: 90, format: 'number' as const, editable: true },
  { field: 'healthScore', label: 'Health Score', width: 110, format: 'number' as const, editable: true },
  { field: 'churnRisk', label: 'Churn Risk', width: 110, format: 'status' as const, editable: true },
  { field: 'recommendation', label: 'Recommendation', width: 260, editable: true, pinned: 'right' as const },
] satisfies NonNullable<ViewTemplateDSL['columns']>;

const OI_FLEET_FILTERS = [
  { field: 'month', label: 'Month', type: 'date' as const },
  { field: 'segment', label: 'Segment', type: 'select' as const, options: oiOptions(OI_FLEET_SEGMENT_OPTIONS) },
  { field: 'anchorSite', label: 'Anchor Site', type: 'text' as const },
  { field: 'churnRisk', label: 'Churn Risk', type: 'select' as const, options: oiOptions(OI_FLEET_RISK_OPTIONS) },
] satisfies NonNullable<ViewTemplateDSL['filters']>;

const OI_FLEET_FORM_FIELDS = [
  { field: 'month', label: 'Month', required: true, type: 'date' as const, group: 'Identity' },
  { field: 'accountName', label: 'Account Name', required: true, span: 2, group: 'Identity' },
  { field: 'churnRisk', label: 'Churn Risk', required: true, type: 'select' as const, options: oiOptions(OI_FLEET_RISK_OPTIONS), group: 'Identity' },
  { field: 'segment', label: 'Segment', type: 'select' as const, options: oiOptions(OI_FLEET_SEGMENT_OPTIONS), group: 'Account' },
  { field: 'anchorSite', label: 'Anchor Site', span: 2, group: 'Account' },
  { field: 'revenue', label: 'Revenue', type: 'number' as const, group: 'Performance' },
  { field: 'serviceJobs', label: 'Service Jobs', type: 'number' as const, group: 'Performance' },
  { field: 'complaintCount', label: 'Complaint Count', type: 'number' as const, group: 'Performance' },
  { field: 'delayCount', label: 'Delay Count', type: 'number' as const, group: 'Performance' },
  { field: 'healthScore', label: 'Health Score', type: 'number' as const, group: 'Performance' },
  { field: 'recommendation', label: 'Recommendation', type: 'textarea' as const, span: 2, group: 'Action' },
] satisfies NonNullable<ViewTemplateDSL['fields']>;

const OI_FLEET_DETAIL_SECTIONS = [
  {
    title: 'Account Summary',
    columns: 6 as const,
    fields: [
      { field: 'month', label: 'Month', format: 'date' },
      { field: 'accountName', label: 'Account Name', span: 2 },
      { field: 'segment', label: 'Segment' },
      { field: 'anchorSite', label: 'Anchor Site', span: 2 },
      { field: 'churnRisk', label: 'Churn Risk', format: 'status' },
      { field: 'healthScore', label: 'Health Score', format: 'number' },
      { field: 'revenue', label: 'Revenue', format: 'currency' },
      { field: 'serviceJobs', label: 'Service Jobs', format: 'number' },
      { field: 'complaintCount', label: 'Complaint Count', format: 'number' },
      { field: 'delayCount', label: 'Delay Count', format: 'number' },
    ],
  },
  {
    title: 'Recommendation',
    columns: 6 as const,
    fields: [{ field: 'recommendation', label: 'Recommendation', span: 6 }],
  },
] satisfies NonNullable<ViewTemplateDSL['sections']>;

export function getSpecializedFallbackTemplate(
  meta: EntityMeta,
  viewType: ViewType,
  lookupSlug?: string,
): ViewTemplateDSL | null {
  // ── FMS per-tab templates (slug override wins) ──────────────────────────────
  if (viewType === 'list') {
    const slug = lookupSlug ?? meta.slug;
    if (slug === 'oi-service-jobs') {
      return {
        columns: OI_SERVICE_JOB_LIST_COLUMNS,
        filters: OI_SERVICE_JOB_FILTERS,
        density: 'compact',
        page: {
          title: 'Service Job Log',
          subtitle: 'Live service-work dataset powering service backlog, incident linkage, and management reporting.',
          searchPlaceholder: 'Job no / site / technician / fleet account / SKU...',
          banners: OI_COMMON_LIST_BANNERS,
        },
      };
    }
    if (slug === 'oi-incidents') {
      return {
        columns: OI_INCIDENT_LIST_COLUMNS,
        filters: OI_INCIDENT_FILTERS,
        density: 'compact',
        page: {
          title: 'Incident Register',
          subtitle: 'Live incident queue with repeat-cluster, root-cause, and owner assignment fields.',
          searchPlaceholder: 'Incident ID / site / category / owner / linked job...',
          banners: OI_COMMON_LIST_BANNERS,
        },
      };
    }
    if (slug === 'oi-warranty-cases') {
      return {
        columns: OI_WARRANTY_LIST_COLUMNS,
        filters: OI_WARRANTY_FILTERS,
        density: 'compact',
        page: {
          title: 'Warranty Case Register',
          subtitle: 'Live warranty and complaint dataset behind quality, response-time, and case analysis flows.',
          searchPlaceholder: 'Case ID / site / SKU / linked job / outcome...',
          banners: OI_COMMON_LIST_BANNERS,
        },
      };
    }
    if (slug === 'oi-inventory-signals') {
      return {
        columns: OI_INVENTORY_LIST_COLUMNS,
        filters: OI_INVENTORY_FILTERS,
        density: 'compact',
        page: {
          title: 'Inventory Signal Register',
          subtitle: 'Live anomaly dataset for cover risk, variance, slow-moving, and abnormal-consumption signals.',
          searchPlaceholder: 'Signal ID / site / SKU / signal type...',
          banners: OI_COMMON_LIST_BANNERS,
        },
      };
    }
    if (slug === 'oi-ehs-events') {
      return {
        columns: OI_EHS_LIST_COLUMNS,
        filters: OI_EHS_FILTERS,
        density: 'compact',
        page: {
          title: 'EHS Event Register',
          subtitle: 'Live safety-event dataset for near misses, unsafe acts, corrective action, and hotspot review.',
          searchPlaceholder: 'Event ID / site / event type / owner...',
          banners: OI_COMMON_LIST_BANNERS,
        },
      };
    }
    if (slug === 'oi-fleet-health-snapshots') {
      return {
        columns: OI_FLEET_LIST_COLUMNS,
        filters: OI_FLEET_FILTERS,
        density: 'compact',
        page: {
          title: 'Fleet Account Health',
          subtitle: 'Live monthly account-health dataset for retention review, churn risk, and service experience.',
          searchPlaceholder: 'Account / segment / anchor site / recommendation...',
          banners: OI_COMMON_LIST_BANNERS,
        },
      };
    }
    if (slug === 'hms-jobs') return HMS_JOB_LIST_TEMPLATE;
    if (slug === 'approval-requests') return APPROVAL_REQUEST_LIST_TEMPLATE;
    if (slug === 'intake-cases') return INTAKE_CASES_LIST_TEMPLATE;
    if (slug === 'finance-bill-entry-list') return FINANCE_BILL_ENTRY_LIST_TEMPLATE;
    if (slug === 'finance-journal-entry-list') return FINANCE_JOURNAL_ENTRY_LIST_TEMPLATE;
    if (slug === 'finance-bill-line-list') return { columns: FINANCE_BILL_LINE_LIST_COLUMNS, density: 'compact' };
    if (slug === 'finance-journal-line-list') return { columns: FINANCE_JOURNAL_LINE_LIST_COLUMNS, density: 'compact' };
    if (slug === 'fms-sea-import-fcl')    return { columns: FMS_SEA_IMPORT_FCL_COLUMNS,    density: 'compact' };
    if (slug === 'fms-sea-import-lcl')    return { columns: FMS_SEA_IMPORT_LCL_COLUMNS,    density: 'compact' };
    if (slug === 'fms-sea-import-consol') return { columns: FMS_SEA_IMPORT_CONSOL_COLUMNS,  density: 'compact' };
    if (slug === 'fms-sea-export-fcl')    return { columns: FMS_SEA_EXPORT_FCL_COLUMNS,    density: 'compact' };
    if (slug === 'fms-sea-export-lcl')    return { columns: FMS_SEA_EXPORT_LCL_COLUMNS,    density: 'compact' };
    if (slug === 'fms-sea-export-consol') return { columns: FMS_SEA_EXPORT_CONSOL_COLUMNS,  density: 'compact' };
    if (slug === 'fms-air-import')        return { columns: FMS_AIR_IMPORT_COLUMNS,         density: 'compact' };
    if (slug === 'fms-air-export')        return { columns: FMS_AIR_EXPORT_COLUMNS,         density: 'compact' };
    if (slug === 'fms-shipments-all')     return { columns: SHIPMENT_LIST_COLUMNS,           density: 'compact' };
  }
  // ── FMS direction-aware detail templates ────────────────────────────────────
  if (viewType === 'detail') {
    const slug = lookupSlug ?? meta.slug;
    if (slug === 'hms-jobs') return HMS_JOB_DETAIL_TEMPLATE;
    if (slug === 'approval-requests') return APPROVAL_REQUEST_DETAIL_TEMPLATE;
    if (slug === 'oi-service-jobs') return { sections: OI_SERVICE_JOB_DETAIL_SECTIONS };
    if (slug === 'oi-incidents') return { sections: OI_INCIDENT_DETAIL_SECTIONS };
    if (slug === 'oi-warranty-cases') return { sections: OI_WARRANTY_DETAIL_SECTIONS };
    if (slug === 'oi-inventory-signals') return { sections: OI_INVENTORY_DETAIL_SECTIONS };
    if (slug === 'oi-ehs-events') return { sections: OI_EHS_DETAIL_SECTIONS };
    if (slug === 'oi-fleet-health-snapshots') return { sections: OI_FLEET_DETAIL_SECTIONS };
    if (slug === 'fms-sea-import-detail') return { sections: FMS_SEA_IMPORT_DETAIL_SECTIONS };
    if (slug === 'fms-sea-export-detail') return { sections: FMS_SEA_EXPORT_DETAIL_SECTIONS };
    if (slug === 'fms-air-import-detail') return { sections: FMS_AIR_IMPORT_DETAIL_SECTIONS };
    if (slug === 'fms-air-export-detail') return { sections: FMS_AIR_EXPORT_DETAIL_SECTIONS };
    if (slug === 'fms-shipments-detail')  return { sections: SHIPMENT_DETAIL_SECTIONS };
  }
  if (viewType === 'form') {
    const slug = lookupSlug ?? meta.slug;
    if (slug === 'oi-service-jobs') return { fields: OI_SERVICE_JOB_FORM_FIELDS };
    if (slug === 'oi-incidents') return { fields: OI_INCIDENT_FORM_FIELDS };
    if (slug === 'oi-warranty-cases') return { fields: OI_WARRANTY_FORM_FIELDS };
    if (slug === 'oi-inventory-signals') return { fields: OI_INVENTORY_FORM_FIELDS };
    if (slug === 'oi-ehs-events') return { fields: OI_EHS_FORM_FIELDS };
    if (slug === 'oi-fleet-health-snapshots') return { fields: OI_FLEET_FORM_FIELDS };
    if (slug === 'hms-job-form') return HMS_JOB_FORM_TEMPLATE;
    if (slug === 'finance-bill-entry-form') return FINANCE_BILL_ENTRY_FORM_TEMPLATE;
    if (slug === 'finance-journal-entry-form') return FINANCE_JOURNAL_ENTRY_FORM_TEMPLATE;
    if (slug === 'finance-bill-line-form') return FINANCE_BILL_LINE_FORM_TEMPLATE;
    if (slug === 'finance-journal-line-form') return FINANCE_JOURNAL_LINE_FORM_TEMPLATE;
    if (slug === 'fms-sea-import-form') return { fields: FMS_SEA_IMPORT_FORM_FIELDS };
    if (slug === 'fms-sea-export-form') return { fields: FMS_SEA_EXPORT_FORM_FIELDS };
    if (slug === 'fms-air-import-form') return { fields: FMS_AIR_IMPORT_FORM_FIELDS };
    if (slug === 'fms-air-export-form') return { fields: FMS_AIR_EXPORT_FORM_FIELDS };
  }
  if (meta.slug === 'shipments') {
    if (viewType === 'list') return { columns: SHIPMENT_LIST_COLUMNS, density: 'compact' };
    if (viewType === 'detail') return { sections: SHIPMENT_DETAIL_SECTIONS };
    if (viewType === 'form') return { fields: SHIPMENT_FORM_FIELDS };
  }

  if (meta.slug === 'pro2-import-shipments' && viewType === 'detail') {
    return {
      sections: translatePro2Sections(PRO2_IMPORT_SHIPMENT_DETAIL_SECTIONS),
      page: {
        actions: [
          { id: 'send-noa', label: pro2Text('Send NOA'), icon: 'mail' },
          { id: 'draft-arrival-email', label: pro2Text('Draft Arrival Email'), icon: 'send', tone: 'outline' },
          { id: 'generate-arrival-note', label: pro2Text('Generate Arrival Note'), icon: 'file', tone: 'outline' },
        ],
      },
    };
  }

  if (meta.slug === 'pro2-export-shipments' && viewType === 'detail') {
    return {
      sections: translatePro2Sections(PRO2_EXPORT_SHIPMENT_DETAIL_SECTIONS),
      page: {
        actions: [
          { id: 'send-shipment-update', label: pro2Text('Send Shipment Update'), icon: 'send' },
          { id: 'draft-bl-followup', label: pro2Text('Draft BL Follow-up'), icon: 'mail', tone: 'outline' },
        ],
      },
    };
  }

  if (meta.slug === 'pro2-export-bookings' && viewType === 'detail') {
    return {
      sections: translatePro2Sections(PRO2_EXPORT_BOOKING_DETAIL_SECTIONS),
      page: {
        actions: [
          { id: 'send-bkg-con', label: pro2Text('Send Bkg Con'), icon: 'send' },
          { id: 'draft-booking-email', label: pro2Text('Draft Booking Email'), icon: 'mail', tone: 'outline' },
          { id: 'generate-booking-note', label: pro2Text('Generate Booking Note'), icon: 'file', tone: 'outline' },
        ],
      },
    };
  }

  if (meta.slug === 'pro2-finance-documents' && viewType === 'detail') {
    return {
      sections: translatePro2Sections(PRO2_FIN_DOCUMENT_DETAIL_SECTIONS),
      page: {
        relatedLists: [
          {
            title: pro2Text('Charge Lines'),
            slug: 'pro2-finance-charges',
            resourcePath: '/cms/pro2-browser/finance/charges',
            queryParam: 'invCnId',
            sourceField: 'id',
            emptyMessage: pro2Text('No charge lines found for this document.'),
          },
        ],
      },
    };
  }

  if (meta.slug === 'pro2-master-companies' && viewType === 'detail') {
    return {
      sections: translatePro2Sections(PRO2_MASTER_COMPANY_DETAIL_SECTIONS),
      page: {
        relatedLists: [
          {
            title: pro2Text('Recent Inv / Cost Note'),
            slug: 'pro2-finance-documents',
            resourcePath: '/cms/pro2-browser/finance/documents',
            queryParam: 'companyCode',
            sourceField: 'companyCode',
            emptyMessage: pro2Text('No finance documents found for this company.'),
          },
          {
            title: pro2Text('Recent Export Bookings'),
            slug: 'pro2-export-bookings',
            resourcePath: '/cms/pro2-browser/export/bookings',
            queryParam: 'shipperCode',
            sourceField: 'companyCode',
            emptyMessage: pro2Text('No export bookings found for this company.'),
          },
        ],
      },
    };
  }

  if (viewType !== 'list') return null;

  if (meta.slug === 'containers') return { columns: CONTAINER_LIST_COLUMNS, density: 'compact' };
  if (meta.slug === 'shipment-cargos') return { columns: CARGO_LIST_COLUMNS, density: 'compact' };
  if (meta.slug === 'shipment-routes') return { columns: ROUTE_LIST_COLUMNS, density: 'compact' };
  if (meta.slug === 'shipment-documents') return { columns: DOCUMENT_LIST_COLUMNS, density: 'compact' };
  if (meta.slug === 'shipment-events') return { columns: EVENT_LIST_COLUMNS, density: 'compact' };
  if (meta.slug === 'ledger-entries') return { columns: LEDGER_ENTRY_LIST_COLUMNS, density: 'compact' };
  if (meta.slug === 'ledger-lines') return { columns: LEDGER_LINE_LIST_COLUMNS, density: 'compact' };
  if (meta.slug === 'job-cost-lines') return { columns: JOB_COST_LINE_LIST_COLUMNS, density: 'compact' };
  if (meta.slug === 'rate-cards') return { columns: RATE_CARD_LIST_COLUMNS, density: 'compact' };
  if (meta.slug === 'rate-tiers') return { columns: RATE_TIER_LIST_COLUMNS, density: 'compact' };
  if (meta.slug === 'pro2-import-shipments') {
    return {
      columns: translatePro2Columns(PRO2_IMPORT_SHIPMENT_COLUMNS),
      density: 'compact',
      page: {
        title: pro2Text('Import Shipments'),
        subtitle: pro2Text('CMS PRO2 sea import Job File read-only browser'),
        searchPlaceholder: pro2Text('Job No / Vessel / Voyage / POL / Agent…'),
        actions: [
          { id: 'send-noa', label: pro2Text('Send NOA'), icon: 'mail' },
          { id: 'draft-arrival-email', label: pro2Text('Draft Arrival Email'), icon: 'send', tone: 'outline' },
          { id: 'generate-arrival-note', label: pro2Text('Generate Arrival Note'), icon: 'file', tone: 'outline' },
        ],
      },
    };
  }
  if (meta.slug === 'pro2-export-shipments') {
    return {
      columns: translatePro2Columns(PRO2_EXPORT_SHIPMENT_COLUMNS),
      density: 'compact',
      page: {
        title: pro2Text('Export Shipments'),
        subtitle: pro2Text('CMS PRO2 sea export Job File read-only browser'),
        searchPlaceholder: pro2Text('Job No / Vessel / Voyage / POL / Agent…'),
        actions: [
          { id: 'send-shipment-update', label: pro2Text('Send Shipment Update'), icon: 'send' },
          { id: 'draft-bl-followup', label: pro2Text('Draft BL Follow-up'), icon: 'mail', tone: 'outline' },
        ],
      },
    };
  }
  if (meta.slug === 'pro2-import-containers') {
    return {
      columns: translatePro2Columns(PRO2_IMPORT_CONTAINER_COLUMNS),
      density: 'compact',
      page: { title: pro2Text('Import Containers'), subtitle: pro2Text('CMS PRO2 sea import container read-only browser'), searchPlaceholder: pro2Text('Job No / Container / Seal / MBL / Vessel…') },
    };
  }
  if (meta.slug === 'pro2-export-containers') {
    return {
      columns: translatePro2Columns(PRO2_EXPORT_CONTAINER_COLUMNS),
      density: 'compact',
      page: { title: pro2Text('Export Containers'), subtitle: pro2Text('CMS PRO2 sea export container read-only browser'), searchPlaceholder: pro2Text('Job No / Container / Seal / MBL / Vessel…') },
    };
  }
  if (meta.slug === 'pro2-import-cargo') {
    return {
      columns: translatePro2Columns(PRO2_IMPORT_CARGO_COLUMNS),
      density: 'compact',
      page: { title: pro2Text('Import Cargo'), subtitle: pro2Text('CMS PRO2 sea import cargo read-only browser'), searchPlaceholder: pro2Text('Job No / BL / Container / Cargo…') },
    };
  }
  if (meta.slug === 'pro2-export-cargo') {
    return {
      columns: translatePro2Columns(PRO2_EXPORT_CARGO_COLUMNS),
      density: 'compact',
      page: { title: pro2Text('Export Cargo'), subtitle: pro2Text('CMS PRO2 sea export cargo read-only browser'), searchPlaceholder: pro2Text('Job No / Booking / BL / Container / Cargo…') },
    };
  }
  if (meta.slug === 'pro2-export-bookings') {
    return {
      columns: translatePro2Columns(PRO2_EXPORT_BOOKING_COLUMNS),
      density: 'compact',
      page: {
        title: pro2Text('Export Bookings'),
        subtitle: pro2Text('CMS PRO2 sea export booking read-only browser'),
        searchPlaceholder: pro2Text('Job No / Booking / Vessel / POL / Dest Port / Cargo…'),
        actions: [
          { id: 'send-bkg-con', label: pro2Text('Send Bkg Con'), icon: 'send' },
          { id: 'draft-booking-email', label: pro2Text('Draft Booking Email'), icon: 'mail', tone: 'outline' },
          { id: 'generate-booking-note', label: pro2Text('Generate Booking Note'), icon: 'file', tone: 'outline' },
        ],
      },
    };
  }
  if (meta.slug === 'pro2-finance-documents') {
    return {
      columns: translatePro2Columns(PRO2_FIN_DOCUMENT_COLUMNS),
      density: 'compact',
      page: {
        title: pro2Text('Invoice / Cost Note'),
        subtitle: pro2Text('CMS PRO2 finance document header read-only browser'),
        searchPlaceholder: pro2Text('Filter this page: Doc No / Company / Job File Id…'),
      },
    };
  }
  if (meta.slug === 'pro2-finance-charges') {
    return {
      columns: translatePro2Columns(PRO2_FIN_CHARGE_COLUMNS),
      density: 'compact',
      page: {
        title: pro2Text('Cost Note Lines'),
        subtitle: pro2Text('CMS PRO2 finance charge-line read-only browser'),
        searchPlaceholder: pro2Text('Filter this page: Doc No / Charge Code / Charge Name / Job File Id…'),
      },
    };
  }
  if (meta.slug === 'pro2-finance-fx') {
    return {
      columns: translatePro2Columns(PRO2_FIN_FX_COLUMNS),
      density: 'compact',
      page: {
        title: pro2Text('Invoice FX'),
        subtitle: pro2Text('CMS PRO2 invoice FX read-only browser'),
        searchPlaceholder: pro2Text('Filter this page: Inv No / Currency / Job File Id…'),
      },
    };
  }
  if (meta.slug === 'pro2-master-companies') {
    return {
      columns: translatePro2Columns(PRO2_MASTER_COMPANY_COLUMNS),
      density: 'compact',
      page: {
        title: pro2Text('Companies'),
        subtitle: pro2Text('CMS PRO2 company master read-only browser'),
        searchPlaceholder: pro2Text('Server search: company name…'),
      },
    };
  }

  return null;
}

// ─── Template Registry ────────────────────────────────────────────────────
//
// TEMPLATE_REGISTRY maps `${entitySlug}:${viewType}` → array of named templates.
//
// Each entity+viewType can have ONE or MORE registered templates.
// The first entry in the array is the "Classic" fallback (id: 'classic').
// Additional entries appear as options in the ScreenTemplateSwitcher dropdown.
//
// Priority chain (highest → lowest):
//   1. Per-screen user preference (screenTemplates store, persisted in localStorage)
//   2. Global designMode 'classic' → first entry with id:'classic'
//   3. API default template (is_default = true)
//   4. Auto-generated DSL from EntityMeta.fields
//
// Convention: keep Classic templates in each entity's view-contracts file.
//

export interface RegisteredTemplate {
  /** Unique id within this slug+viewType — e.g. 'classic', 'ops-focus', 'billing' */
  id: string;
  /** Short human label shown in the ScreenTemplateSwitcher */
  label: string;
  /** Optional icon name from lucide-react */
  icon?: 'layout-list' | 'layout-grid' | 'file-text' | 'calculator' | 'truck' | 'package';
  template: ViewTemplateDSL;
}

// ── HMS Ops-Focus list template ─────────────────────────────────────────────
// A trimmed view that only surfaces the fields operations staff care about:
// container, customer, terminal, vessel, ETA, LFD, pickup/delivery, driver.
const HMS_JOB_LIST_OPS_TEMPLATE: ViewTemplateDSL = {
  list: { lineMode: '1-line' },
  density: 'compact',
  columns: [
    { field: 'job_no',           label: 'Job No',        width: 160, pinned: 'left', sortable: true },
    { field: 'job_type',         label: 'Type',          width: 80 },
    { field: 'status',           label: 'Status',        width: 100, format: 'status' },
    { field: 'container_no',     label: 'Container',     width: 150 },
    { field: 'customer_name',    label: 'Customer',      width: 180, sortable: true },
    { field: 'terminal',         label: 'Terminal',      width: 160 },
    { field: 'vessel_name',      label: 'Vessel',        width: 150 },
    { field: 'eta',              label: 'ETA',           width: 110, format: 'date', sortable: true },
    { field: 'lfd',              label: 'LFD',           width: 110, format: 'date', sortable: true },
    { field: 'pickup_date',      label: 'Pickup',        width: 110, format: 'date' },
    { field: 'delivery_date',    label: 'Delivery',      width: 110, format: 'date' },
    { field: 'driver_name',      label: 'Driver',        width: 140 },
    { field: 'truck_plate',      label: 'Truck',         width: 110 },
  ],
};

// ── Generic "jobs" entity (used by EntityListPage at /e/jobs) ─────────────
const JOBS_CLASSIC_LIST_TEMPLATE: ViewTemplateDSL = {
  columns: [
    { field: 'job_number',   label: 'Job No',    width: 160, sortable: true },
    { field: 'job_type',     label: 'Type',      width: 80  },
    { field: 'status',       label: 'Status',    width: 100 },
    { field: 'customer_id',  label: 'Customer',  width: 180 },
    { field: 'origin',       label: 'Origin',    width: 140 },
    { field: 'destination',  label: 'Destination', width: 140 },
    { field: 'created_at',   label: 'Created',   width: 120, format: 'date' },
  ],
};

const JOBS_COMPACT_LIST_TEMPLATE: ViewTemplateDSL = {
  columns: [
    { field: 'job_number',   label: 'Job No',    width: 160, sortable: true },
    { field: 'job_type',     label: 'Type',      width: 80  },
    { field: 'status',       label: 'Status',    width: 100 },
    { field: 'created_at',   label: 'Created',   width: 120, format: 'date' },
  ],
};

const JOBS_DETAIL_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'Job Identity',
      columns: 6,
      fields: [
        { field: 'jobNumber', label: 'Job No', span: 2 },
        { field: 'jobType', label: 'Type' },
        { field: 'status', label: 'Status', format: 'status' },
        { field: 'customerId', label: 'Customer', span: 2 },
      ],
    },
    {
      title: 'Lifecycle',
      columns: 6,
      fields: [
        { field: 'createdAt', label: 'Created At', format: 'datetime' },
        { field: 'updatedAt', label: 'Updated At', format: 'datetime' },
      ],
    },
  ],
};

const JOBS_CLASSIC_DETAIL_TEMPLATE: ViewTemplateDSL = {
  sections: JOBS_DETAIL_TEMPLATE.sections,
};

const JOBS_FORM_TEMPLATE: ViewTemplateDSL = {
  fields: [
    { field: 'jobNumber', label: 'Job No', placeholder: 'Auto-generated on create', span: 2, group: 'Job Setup' },
    {
      field: 'jobType',
      label: 'Job Type',
      type: 'select',
      required: true,
      options: [
        { label: 'IMP', value: 'IMP' },
        { label: 'EXP', value: 'EXP' },
        { label: 'LCL', value: 'LCL' },
        { label: 'FCL', value: 'FCL' },
        { label: 'SEA_IMPORT', value: 'SEA_IMPORT' },
      ],
      group: 'Job Setup',
    },
    {
      field: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { label: 'draft', value: 'draft' },
        { label: 'submitted', value: 'submitted' },
        { label: 'assigned', value: 'assigned' },
        { label: 'in_progress', value: 'in_progress' },
        { label: 'completed', value: 'completed' },
        { label: 'cancelled', value: 'cancelled' },
      ],
      group: 'Job Setup',
    },
    {
      field: 'customerId',
      label: 'Customer',
      type: 'lookup',
      span: 2,
      group: 'Assignment',
    },
  ],
};

const APPROVAL_REQUEST_LIST_TEMPLATE: ViewTemplateDSL = {
  density: 'compact',
  columns: [
    { field: 'title', label: 'Title', width: 260, sortable: true, pinned: 'left' },
    { field: 'status', label: 'Status', width: 110, format: 'status', sortable: true },
    { field: 'priority', label: 'Priority', width: 90, sortable: true },
    { field: 'reviewerRole', label: 'Reviewer', width: 130, sortable: true },
    { field: 'requestType', label: 'Request Type', width: 150, sortable: true },
    { field: 'taskCategory', label: 'Task Category', width: 140, sortable: true },
    { field: 'urgency', label: 'Urgency', width: 110, sortable: true },
    { field: 'confidenceDisplay', label: 'Confidence', width: 120, sortable: true },
    { field: 'createdAt', label: 'Created', width: 180, format: 'datetime', sortable: true },
  ],
  filters: [
    {
      field: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
        { label: 'Escalated', value: 'escalated' },
        { label: 'Expired', value: 'expired' },
      ],
    },
    {
      field: 'priority',
      label: 'Priority',
      type: 'select',
      options: [
        { label: 'P0', value: 'P0' },
        { label: 'P1', value: 'P1' },
        { label: 'P2', value: 'P2' },
      ],
    },
    {
      field: 'reviewerRole',
      label: 'Reviewer',
      type: 'text',
      placeholder: 'manager / supervisor / admin',
    },
  ],
  page: {
    title: 'Approval Queue',
    subtitle: 'Human review queue with council context, urgency, confidence, and risk signals.',
    searchPlaceholder: 'Search title / reviewer / request type / decision context...',
  },
};

const APPROVAL_REQUEST_DETAIL_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'Approval Request',
      columns: 3,
      fields: [
        { field: 'title', label: 'Title', span: 2 },
        { field: 'status', label: 'Status' },
        { field: 'priority', label: 'Priority' },
        { field: 'requestType', label: 'Request Type' },
        { field: 'reviewerRole', label: 'Reviewer Role' },
        { field: 'requesterRole', label: 'Requester Role' },
        { field: 'createdAt', label: 'Created At', format: 'datetime' },
        { field: 'timeoutAt', label: 'Timeout At', format: 'datetime' },
        { field: 'escalationRole', label: 'Escalation Role' },
      ],
    },
    {
      title: 'Decision Outcome',
      columns: 3,
      fields: [
        { field: 'decidedBy', label: 'Decided By' },
        { field: 'decidedAt', label: 'Decided At', format: 'datetime' },
        { field: 'decisionReason', label: 'Decision Reason', span: 3 },
      ],
    },
    {
      title: 'Linked Records',
      columns: 3,
      fields: [
        { field: 'jobId', label: 'Job Id' },
        { field: 'workItemId', label: 'Work Item Id' },
        { field: 'entityType', label: 'Entity Type' },
        { field: 'entityId', label: 'Entity Id', span: 2 },
      ],
    },
    {
      title: 'Raw Payload',
      columns: 1,
      fields: [
        { field: 'description', label: 'Description', span: 1 },
        { field: 'payload', label: 'Payload', span: 1 },
      ],
    },
  ],
};

const TEMPLATE_REGISTRY: Record<string, RegisteredTemplate[]> = {
  // ── Generic jobs entity ───────────────────────────────────────────────
  'jobs:list': [
    { id: 'classic', label: 'Classic', icon: 'layout-list', template: JOBS_CLASSIC_LIST_TEMPLATE },
    { id: 'compact', label: 'Compact', icon: 'layout-grid', template: JOBS_COMPACT_LIST_TEMPLATE },
  ],
  'jobs:detail': [
    { id: 'classic', label: 'Classic', icon: 'file-text', template: JOBS_CLASSIC_DETAIL_TEMPLATE },
    { id: 'overview', label: 'Overview', icon: 'layout-grid', template: JOBS_DETAIL_TEMPLATE },
  ],
  'jobs:form': [
    { id: 'default', label: 'Default', icon: 'file-text', template: JOBS_FORM_TEMPLATE },
  ],
  // ── HMS — Container Trucking ──────────────────────────────────────────
  'hms-jobs:list': [
    { id: 'classic',   label: 'Classic',      icon: 'layout-list', template: HMS_JOB_LIST_CLASSIC_TEMPLATE },
    { id: 'ops-focus', label: 'Ops Focus',    icon: 'truck',       template: HMS_JOB_LIST_OPS_TEMPLATE },
  ],
  'hms-jobs:detail': [
    { id: 'classic',   label: 'Classic',      icon: 'file-text',   template: HMS_JOB_DETAIL_CLASSIC_TEMPLATE },
  ],
  // ── FMS — Freight (Sea) ───────────────────────────────────────────────
  'fms-sea-import-fcl:list':    [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FMS_SEA_CLASSIC_LIST_TEMPLATE }],
  'fms-sea-import-lcl:list':    [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FMS_SEA_CLASSIC_LIST_TEMPLATE }],
  'fms-sea-import-consol:list': [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FMS_SEA_CLASSIC_LIST_TEMPLATE }],
  'fms-sea-export-fcl:list':    [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FMS_SEA_CLASSIC_LIST_TEMPLATE }],
  'fms-sea-export-lcl:list':    [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FMS_SEA_CLASSIC_LIST_TEMPLATE }],
  'fms-sea-export-consol:list': [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FMS_SEA_CLASSIC_LIST_TEMPLATE }],
  'fms-sea-import-detail:detail': [{ id: 'classic', label: 'Classic', icon: 'file-text', template: FMS_SEA_IMPORT_DETAIL_CLASSIC_TEMPLATE }],
  'fms-sea-export-detail:detail': [{ id: 'classic', label: 'Classic', icon: 'file-text', template: FMS_SEA_EXPORT_DETAIL_CLASSIC_TEMPLATE }],
  // ── FMS — Freight (Air) ───────────────────────────────────────────────
  'fms-air-import:list':          [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FMS_AIR_CLASSIC_LIST_TEMPLATE }],
  'fms-air-export:list':          [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FMS_AIR_CLASSIC_LIST_TEMPLATE }],
  'fms-air-import-detail:detail': [{ id: 'classic', label: 'Classic', icon: 'file-text',   template: FMS_AIR_IMPORT_DETAIL_CLASSIC_TEMPLATE }],
  'fms-air-export-detail:detail': [{ id: 'classic', label: 'Classic', icon: 'file-text',   template: FMS_AIR_EXPORT_DETAIL_CLASSIC_TEMPLATE }],
  // ── Finance / Accts — Lists ───────────────────────────────────────────
  'finance-bill-entry-list:list':    [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FINANCE_BILL_ENTRY_LIST_CLASSIC_TEMPLATE }],
  'finance-journal-entry-list:list': [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FINANCE_JOURNAL_ENTRY_LIST_CLASSIC_TEMPLATE }],
  'finance-ar-receipt-list:list':    [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FINANCE_AR_RECEIPT_LIST_CLASSIC_TEMPLATE }],
  'finance-ap-payment-list:list':    [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: FINANCE_AP_PAYMENT_LIST_CLASSIC_TEMPLATE }],
  // ── Finance / Accts — Details (header + row-editable lines) ──────────
  'finance-bill-entry-list:detail':    [{ id: 'classic', label: 'Classic', icon: 'calculator', template: FINANCE_BILL_ENTRY_DETAIL_CLASSIC_TEMPLATE }],
  'finance-journal-entry-list:detail': [{ id: 'classic', label: 'Classic', icon: 'calculator', template: FINANCE_JOURNAL_ENTRY_DETAIL_CLASSIC_TEMPLATE }],
  'finance-ar-receipt-list:detail':    [{ id: 'classic', label: 'Classic', icon: 'calculator', template: FINANCE_AR_RECEIPT_DETAIL_CLASSIC_TEMPLATE }],
  'finance-ap-payment-list:detail':    [{ id: 'classic', label: 'Classic', icon: 'calculator', template: FINANCE_AP_PAYMENT_DETAIL_CLASSIC_TEMPLATE }],
  // ── Approvals ────────────────────────────────────────────────────────
  'approval-requests:list':   [{ id: 'classic', label: 'Classic', icon: 'layout-list', template: APPROVAL_REQUEST_LIST_TEMPLATE }],
  'approval-requests:detail': [{ id: 'classic', label: 'Classic', icon: 'file-text',   template: APPROVAL_REQUEST_DETAIL_TEMPLATE }],
};

/**
 * Returns all registered templates for a given slug+viewType.
 * Returns [] when no templates are registered (entity not yet in registry).
 */
export function getRegisteredTemplates(
  slug: string,
  viewType: ViewType,
): RegisteredTemplate[] {
  return TEMPLATE_REGISTRY[`${slug}:${viewType}`] ?? [];
}

/**
 * Returns a specific registered template by id.
 * Falls back to first entry if id not found.
 */
export function getRegisteredTemplate(
  slug: string,
  viewType: ViewType,
  id: string,
): ViewTemplateDSL | null {
  const entries = TEMPLATE_REGISTRY[`${slug}:${viewType}`] ?? [];
  const found = entries.find((e) => e.id === id) ?? entries[0];
  return found?.template ?? null;
}

/**
 * Returns the Classic-layout DSL for the given entity slug + view type,
 * or `null` if no Classic variant has been defined yet.
 *
 * Called by `useViewTemplate` when the user has switched to Classic mode.
 * The result is synchronous — no API call, no network latency.
 *
 * @deprecated prefer getRegisteredTemplate(slug, viewType, 'classic')
 */
export function getClassicTemplate(
  slug: string,
  viewType: ViewType,
): ViewTemplateDSL | null {
  return getRegisteredTemplate(slug, viewType, 'classic');
}
